import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import { supabase } from '../supabase';
import { DraftSaver } from '../utils/draftSaver';
import './AdminDashboard.css';

// Helper function to determine primary currency and format price
const getPrimaryCurrencyAndPrice = (product) => {
  // Check which currency has a price value (check all currencies and use the one that was actually set)
  const currencies = [
    { key: 'price_sar', currency: 'KSA', symbol: 'KSA' },
    { key: 'price_aed', currency: 'AED', symbol: 'AED' },
    { key: 'price_gbp', currency: 'GBP', symbol: '£' },
    { key: 'price_usd', currency: 'USD', symbol: '$' }
  ];
  
  for (const curr of currencies) {
    const price = product[curr.key];
    const originalPrice = product[`original_${curr.key}`];
    
    if (price && parseFloat(price) > 0) {
      return { 
        currency: curr.currency, 
        symbol: curr.symbol, 
        price: parseFloat(price), 
        originalPrice: originalPrice ? parseFloat(originalPrice) : null 
      };
    }
  }
  
  // Fallback to USD if no price is set
  return { currency: 'USD', symbol: '$', price: 0, originalPrice: null };
};

// Helper function to get currency symbol for a specific country
const getCurrencySymbolForCountry = (countryName) => {
  const country = countryName?.toLowerCase() || '';
  
  if (country.includes('united states') || country.includes('usa') || country.includes('america')) {
    return '$';
  } else if (country.includes('united kingdom') || country.includes('uk') || country.includes('britain') || country.includes('england')) {
    return '£';
  } else if (country.includes('uae') || country.includes('united arab emirates') || country.includes('emirates')) {
    return 'د.إ';
  } else if (country.includes('saudi') || country.includes('ksa') || country.includes('arabia')) {
    return 'ر.س';
  }
  
  // Default fallback to GBP
  return '£';
};

const AdminDashboard = () => {
  const { currency } = useCurrency();
  const [adminInfo, setAdminInfo] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [vendorRequests, setVendorRequests] = useState([]);
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormData, setProductFormData] = useState({
    name: '',
    description: '',
    price_usd: '',
    price_gbp: '',
    price_aed: '',
    price_sar: '',
    stock: '',
    category: '',
    status: 'active',
    image_url: ''
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    status: 'active'
  });
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    status: 'active'
  });
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorAction, setVendorAction] = useState('');
  const [notification, setNotification] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [shippingRates, setShippingRates] = useState([]);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [editingShipping, setEditingShipping] = useState(null);
  const [shippingFormData, setShippingFormData] = useState({
    country_name: '',
    country_code: '',
    shipping_cost: '',
    estimated_delivery_days: 7,
    is_active: true
  });
  const [contactQueries, setContactQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [queryFormData, setQueryFormData] = useState({
    status: '',
    admin_notes: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [adminProducts, setAdminProducts] = useState([]);
  const [showAdminProductModal, setShowAdminProductModal] = useState(false);
  const [editingAdminProduct, setEditingAdminProduct] = useState(null);
  const [adminProductFormData, setAdminProductFormData] = useState({
    name: '',
    description: '',
    long_description: '',
    price_usd: '',
    price_gbp: '',
    price_aed: '',
    price_sar: '',
    original_price_usd: '',
    original_price_gbp: '',
    original_price_aed: '',
    original_price_sar: '',
    stock: '',
    category: '',
    subcategory: '',
    brand: '',
    sku: '',
    status: 'active',
    image_url: '',
    video_url: '',
    weight: '',
    weight_unit: 'g',
    dimensions: '',
    dimension_unit: 'cm',
    countries: [],
    color: '',
    material: '',
    warranty: '',
    shipping_info: '',
    return_policy: '',
    is_featured: false,
    is_bestseller: false,
    is_new_arrival: false,
    meta_title: '',
    meta_description: '',
    seo_keywords: '',
    vendor_id: null
  });
  const [addProductFormData, setAddProductFormData] = useState({
    name: '',
    price_usd: '',
    price_gbp: '',
    price_aed: '',
    price_sar: '',
    original_price_usd: '',
    original_price_gbp: '',
    original_price_aed: '',
    original_price_sar: '',
    category: '',
    brand: '',
    stock: '',
    sku: '',
    description: '',
    long_description: '',
    image_url: '',
    gallery_images: [],
    video_url: '',
    video_thumbnail: '',
    specifications: {},
    features: [],
    tags: [],
    weight: '',
    weight_unit: 'g',
    dimensions: '',
    dimension_unit: 'cm',
    countries: [],
    color: '',
    material: '',
    warranty: '',
    shipping_info: '',
    return_policy: '',
    is_featured: false,
    is_bestseller: false,
    is_new_arrival: false,
    meta_title: '',
    meta_description: '',
    seo_keywords: '',
    has_variations: false,
    variations: [],
    subcategories: [],
    newSubcategory: ''
  });
  const [changeCredentialsFormData, setChangeCredentialsFormData] = useState({
    currentUsername: '',
    newUsername: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Draft saver for add product form
  const [draftSaver] = useState(() => new DraftSaver('admin_add_product', 5000));
  
  // Test function for draft save popup
  const testDraftSavePopup = () => {
    const testDraftSaver = new DraftSaver('admin_add_product', 5000);
    testDraftSaver.testDraftSavePopup(addProductFormData);
  };

  // Test function for warning banner
  const testWarningBanner = () => {
    const testDraftSaver = new DraftSaver('admin_add_product', 5000);
    testDraftSaver.showUnsavedChangesWarning();
  };
  const [hasDraft, setHasDraft] = useState(false);
  const [draftInfo, setDraftInfo] = useState(null);

  // Featured Category state variables
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('Electronics');
  const [galleryImages, setGalleryImages] = useState([]);
  const [specifications, setSpecifications] = useState([{ key: '', value: '' }]);
  const [features, setFeatures] = useState(['']);
  const [tags, setTags] = useState(['']);
  const [variations, setVariations] = useState([]);
  const [variationTypes, setVariationTypes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [heroBackgroundImage, setHeroBackgroundImage] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const navigate = useNavigate();

  // Hot Deals state variables
  const [hotDealsProducts, setHotDealsProducts] = useState({
    hot_products: [],
    todays_deals: [],
    new_arrivals: [],
    exclusive_offers: []
  });
  const [hotDealsLoading, setHotDealsLoading] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Check if admin is logged in
    const adminLoggedIn = localStorage.getItem('adminLoggedIn');
    const adminUsername = localStorage.getItem('adminUsername');

    if (!adminLoggedIn || !adminUsername) {
      navigate('/admin/login');
      return;
    }

    setAdminInfo({
      username: adminUsername
    });

    // Load admin data
    loadAdminData();
  }, [navigate]);

  // Setup draft auto-save and beforeunload when add product section is active
  useEffect(() => {
    if (activeSection === 'add-product') {
      // Load draft if exists
      loadDraftIfExists();
      
      // Start auto-save
      draftSaver.startAutoSave(addProductFormData, (savedData) => {
        console.log('Auto-saved draft:', savedData);
      });
      
      // Setup beforeunload event
      const cleanupBeforeUnload = draftSaver.setupBeforeUnload(addProductFormData);
      
      // Setup navigation protection
      const handleBeforeNavigate = (e) => {
        if (draftSaver.hasUnsavedChanges(addProductFormData)) {
          e.preventDefault();
          draftSaver.showDraftSavePopupManually(addProductFormData);
        }
      };
      
      // Listen for navigation attempts
      window.addEventListener('beforeunload', handleBeforeNavigate);
      
      return () => {
        draftSaver.stopAutoSave();
        cleanupBeforeUnload();
        window.removeEventListener('beforeunload', handleBeforeNavigate);
      };
    }
  }, [activeSection, addProductFormData]);

  const loadAdminData = async () => {
    setLoading(true);
    try {

      // Load orders with vendor information
      console.log('🔄 Loading orders from Supabase...');
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.error('❌ Error loading orders:', ordersError);
        console.error('Error details:', {
          message: ordersError.message,
          details: ordersError.details,
          hint: ordersError.hint
        });
        setOrders([]);
      } else {
        console.log(`✅ Loaded ${ordersData?.length || 0} orders from database`);
        console.log('Raw orders data:', ordersData);
        
        // Transform orders data to match the expected format and include items
        const transformedOrders = await Promise.all((ordersData || []).map(async (order) => {
          // Parse items from the order (items are stored as JSON string in the database)
          let orderItems = [];
          try {
            if (order.items) {
              orderItems = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
            }
          } catch (error) {
            console.error('Error parsing order items:', error);
            orderItems = [];
          }

          return {
            id: order.id, // Use the actual database ID for deletion
            orderNumber: order.order_number || `ORD-${order.id}`, // Use order_number for display
            customer: `${order.customer_first_name || 'Unknown'} ${order.customer_last_name || ''}`.trim(),
            customerEmail: order.customer_email,
            customerPhone: order.customer_phone,
            customerAddress: order.customer_address,
            customerCity: order.customer_city,
            customerState: order.customer_state,
            customerZipCode: order.customer_zip_code,
            customerCountry: order.customer_country, // Store country for currency detection
            vendor: 'N/A', // Will be updated if we have vendor info
            total: parseFloat(order.total || 0).toFixed(2),
            status: order.status || 'pending',
            paymentStatus: order.payment_status || 'pending',
            date: new Date(order.created_at).toLocaleDateString(),
            createdAt: order.created_at,
            items: orderItems || []
          };
        }));
        
        console.log('Transformed orders:', transformedOrders);
        setOrders(transformedOrders);
      }

      // Load products with vendor information
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Raw products data:', productsData);
      console.log('Products error:', productsError);

      // Debug: Check what vendors exist
      const { data: allVendors, error: vendorsError } = await supabase
        .from('vendors')
        .select('id, business_name, email');
      
      console.log('All vendors in database:', allVendors);
      console.log('Vendors error:', vendorsError);

      if (productsError) {
        console.error('Error loading products:', productsError);
      } else {
        // Get vendor names for products
        const productsWithVendorNames = await Promise.all(
          (productsData || []).map(async (product) => {
            console.log('Processing product:', product.id, 'vendor_id:', product.vendor_id);
            
            if (product.vendor_id) {
              const { data: vendorData, error: vendorError } = await supabase
                .from('vendors')
                .select('business_name, email')
                .eq('id', product.vendor_id)
                .single();
              
              console.log('Vendor data for product', product.id, ':', vendorData, 'Error:', vendorError);
              
              if (vendorError) {
                console.error('Error fetching vendor for product', product.id, ':', vendorError);
                return {
                  ...product,
                  vendor: 'Vendor Not Found'
                };
              }
              
              // Use business_name field (the only one that exists in the table)
              const businessName = vendorData?.business_name || `Vendor ID: ${product.vendor_id}`;
              
              return {
                ...product,
                vendor: businessName
              };
            }
            return {
              ...product,
              vendor: 'No Vendor Assigned'
            };
          })
        );
        console.log('Final products with vendor names:', productsWithVendorNames);
        setProducts(productsWithVendorNames);
      }

      // Load customers from the customers table
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (customersError) {
        console.error('Error loading customers:', customersError);
        setUsers([]);
      } else {
        console.log('Customers loaded:', customersData);
        // Transform customers data to match the expected format
        const transformedCustomers = (customersData || []).map(customer => ({
          id: customer.id,
          name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown',
          email: customer.email || '',
          phone: customer.phone || '',
          status: customer.status || 'active',
          created_at: customer.created_at,
          orders: 0, // Will be calculated separately if needed
          totalSpent: 0 // Will be calculated separately if needed
        }));
        setUsers(transformedCustomers);
      }

      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
      } else {
        console.log('Categories loaded:', categoriesData);
        setCategories(categoriesData || []);
      }

      // Load subcategories
      const { data: subcategoriesData, error: subcategoriesError } = await supabase
        .from('subcategories')
        .select('*, categories(name)')
        .order('name', { ascending: true });

      if (subcategoriesError) {
        console.error('Error loading subcategories:', subcategoriesError);
      } else {
        console.log('Subcategories loaded:', subcategoriesData);
        setSubcategories(subcategoriesData || []);
      }

      // Load vendor requests (pending vendors)
      const { data: vendorRequestsData, error: vendorRequestsError } = await supabase
        .from('vendors')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (vendorRequestsError) {
        console.error('Error loading vendor requests:', vendorRequestsError);
      } else {
        console.log('Vendor requests loaded:', vendorRequestsData);
        setVendorRequests(vendorRequestsData || []);
      }

      // Load approved vendors
      const { data: approvedVendorsData, error: approvedVendorsError } = await supabase
        .from('vendors')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (approvedVendorsError) {
        console.error('Error loading approved vendors:', approvedVendorsError);
      } else {
        console.log('Approved vendors loaded:', approvedVendorsData);
        setApprovedVendors(approvedVendorsData || []);
      }

      // Load shipping rates
      const { data: shippingRatesData, error: shippingRatesError } = await supabase
        .from('shipping_rates')
        .select('*')
        .order('country_name', { ascending: true });

      if (shippingRatesError) {
        console.error('Error loading shipping rates:', shippingRatesError);
      } else {
        console.log('Shipping rates loaded:', shippingRatesData);
        setShippingRates(shippingRatesData || []);
      }

      // Load contact queries
      const { data: queriesData, error: queriesError } = await supabase
        .from('contact_queries')
        .select('*')
        .order('created_at', { ascending: false });

      if (queriesError) {
        console.error('Error loading contact queries:', queriesError);
      } else {
        setContactQueries(queriesData || []);
      }

      // Load admin products (products with no vendor_id or admin-created products)
      const { data: adminProductsData, error: adminProductsError } = await supabase
        .from('products')
        .select('*')
        .or('vendor_id.is.null,vendor_id.eq.0')
        .order('created_at', { ascending: false });

      if (adminProductsError) {
        console.error('Error loading admin products:', adminProductsError);
      } else {
        console.log('Admin products loaded:', adminProductsData);
        setAdminProducts(adminProductsData || []);
      }

      // Load site settings
      await loadSiteSettings();

      // Load hot deals products
      await loadHotDealsProducts();

    } catch (error) {
      console.error('Error loading admin data:', error);
      showNotification('An error occurred while loading data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Site settings functions
  const loadSiteSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*');
      
      if (error) {
        console.error('Error loading site settings:', error);
        return;
      }

      const settingsMap = {};
      data?.forEach(setting => {
        settingsMap[setting.setting_key] = setting.setting_value;
      });
      
      setSiteSettings(settingsMap);
      setHeroBackgroundImage(settingsMap.hero_background_image || '');
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const loadHotDealsProducts = async () => {
    setHotDealsLoading(true);
    try {
      const sections = ['hot_products', 'todays_deals', 'new_arrivals', 'exclusive_offers'];
      const newHotDealsProducts = {};

      for (const section of sections) {
        let filterField = '';
        switch (section) {
          case 'hot_products':
            filterField = 'is_hot_product';
            break;
          case 'todays_deals':
            filterField = 'is_todays_deal';
            break;
          case 'new_arrivals':
            filterField = 'is_new_arrival';
            break;
          case 'exclusive_offers':
            filterField = 'is_exclusive_offer';
            break;
        }

        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('status', 'active')
          .eq(filterField, true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error(`Error loading ${section}:`, error);
          newHotDealsProducts[section] = [];
        } else {
          newHotDealsProducts[section] = data || [];
        }
      }

      setHotDealsProducts(newHotDealsProducts);
    } catch (error) {
      console.error('Error loading hot deals products:', error);
    } finally {
      setHotDealsLoading(false);
    }
  };

  const handleHeroBackgroundUpload = async (file) => {
    try {
      setSettingsLoading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `hero-bg-${Date.now()}.${fileExt}`;
      const filePath = fileName; // Remove the extra 'site-images/' prefix

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('site-images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('site-images')
        .getPublicUrl(filePath);

      // Update site settings in database - first delete existing, then insert new
      await supabase
        .from('site_settings')
        .delete()
        .eq('setting_key', 'hero_background_image');
        
      const { error: updateError } = await supabase
        .from('site_settings')
        .insert({
          setting_key: 'hero_background_image',
          setting_value: publicUrl,
          setting_type: 'image_url',
          description: 'Background image for the hero section on homepage'
        });

      if (updateError) {
        throw updateError;
      }

      setHeroBackgroundImage(publicUrl);
      setSiteSettings(prev => ({ ...prev, hero_background_image: publicUrl }));
      
      // Trigger event for homepage to refresh background
      window.dispatchEvent(new CustomEvent('hero-background-updated'));
      
      showNotification('Hero background image updated successfully!', 'success');
    } catch (error) {
      console.error('Error uploading hero background:', error);
      showNotification('Error uploading background image. Please try again.', 'error');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminUsername');
    navigate('/admin/login');
  };

  const handleViewOrder = (order) => {
    console.log('Order data:', order); // Debug log
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
  };

  const handleEditProduct = (product) => {
    // Populate the add product form data with the existing product data
    setAddProductFormData({
      name: product.name || '',
      price_usd: product.price_usd || '',
      price_gbp: product.price_gbp || '',
      price_aed: product.price_aed || '',
      price_sar: product.price_sar || '',
      original_price_usd: product.original_price_usd || '',
      original_price_gbp: product.original_price_gbp || '',
      original_price_aed: product.original_price_aed || '',
      original_price_sar: product.original_price_sar || '',
      category: product.category || '',
      brand: product.brand || '',
      stock: product.stock || '',
      sku: product.sku || '',
      description: product.description || '',
      long_description: product.long_description || '',
      image_url: product.image_url || '',
      gallery_images: product.gallery_images || [],
      video_url: product.video_url || '',
      video_thumbnail: product.video_thumbnail || '',
      specifications: product.specifications || {},
      features: product.features || [],
      tags: product.tags || [],
      weight: product.weight || '',
      weight_unit: product.weight_unit || 'g',
      dimensions: product.dimensions || '',
      dimension_unit: product.dimension_unit || 'cm',
      countries: product.countries || [],
      color: product.color || '',
      material: product.material || '',
      warranty: product.warranty || '',
      shipping_info: product.shipping_info || '',
      return_policy: product.return_policy || '',
      is_featured: product.is_featured || false,
      is_bestseller: product.is_bestseller || false,
      is_new_arrival: product.is_new_arrival || false,
      meta_title: product.meta_title || '',
      meta_description: product.meta_description || '',
      seo_keywords: product.seo_keywords || '',
      has_variations: product.has_variations || false,
      variations: product.variations || [],
              subcategories: product.subcategory ? (() => {
                try {
                  return typeof product.subcategory === 'string' ? JSON.parse(product.subcategory) : [product.subcategory];
                } catch (e) {
                  console.warn('Error parsing subcategory JSON:', e);
                  return [product.subcategory];
                }
              })() : [],
        newSubcategory: ''
    });

    // Set the gallery images, specifications, features, and tags
    setGalleryImages(product.gallery_images || []);
    setSpecifications(product.specifications ? Object.entries(product.specifications).map(([key, value]) => ({ key, value })) : [{ key: '', value: '' }]);
    setFeatures(product.features && product.features.length > 0 ? product.features : ['']);
    setTags(product.tags && product.tags.length > 0 ? product.tags : ['']);
    setVariations(product.variations || []);
    setVariationTypes(product.variations ? product.variations.map(v => v.type) : []);

    // Store the editing product ID for reference
    setEditingProduct(product);

    // Redirect to the add product section
    setActiveSection('add-product');
  };

  const handleDeleteProduct = async (product) => {
    setDeleteTarget(product);
    setDeleteType('product');
    setShowDeleteModal(true);
  };

  const handleToggleFeatured = async (product) => {
    try {
      const newFeaturedStatus = !product.featured;
      
      const { error } = await supabase
        .from('products')
        .update({ featured: newFeaturedStatus })
        .eq('id', product.id);

      if (error) {
        console.error('Error updating featured status:', error);
        showNotification('Failed to update featured status', 'error');
      } else {
        // Update the local state
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.id === product.id 
              ? { ...p, featured: newFeaturedStatus }
              : p
          )
        );
        
        showNotification(
          `Product ${newFeaturedStatus ? 'pinned to' : 'unpinned from'} featured section`, 
          'success'
        );
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
      showNotification('Failed to update featured status', 'error');
    }
  };

  const handleTogglePin = async (product) => {
    try {
      const currentPinStatus = product.is_featured_category || false;
      
      // If trying to add a product and featured category already has 4 products for this currency and category, show error
      if (!currentPinStatus) {
        // Count current featured category products for the same currency and category
        const currentFeaturedCount = products.filter(p => {
          if (!p.is_featured_category) return false;
          
          // Check if product has price in selected currency
          let hasPriceInCurrency = false;
          switch (selectedCurrency) {
            case 'USD':
              hasPriceInCurrency = p.price_usd && parseFloat(p.price_usd) > 0;
              break;
            case 'GBP':
              hasPriceInCurrency = p.price_gbp && parseFloat(p.price_gbp) > 0;
              break;
            case 'AED':
              hasPriceInCurrency = p.price_aed && parseFloat(p.price_aed) > 0;
              break;
            case 'SAR':
              hasPriceInCurrency = p.price_sar && parseFloat(p.price_sar) > 0;
              break;
            default:
              hasPriceInCurrency = true;
          }
          
          // Check if product is in selected category using flexible matching
          let isInSelectedCategory = false;
          if (p.category) {
            const productCategory = p.category.toLowerCase().trim();
            const selectedCat = selectedCategoryFilter.toLowerCase().trim();
            
            // Exact match
            isInSelectedCategory = productCategory === selectedCat;
            
            // If no exact match, try partial matching for similar categories
            if (!isInSelectedCategory) {
              // Handle variations like "Pet Care" vs "Pet Supplies"
              if (selectedCat.includes('pet') || productCategory.includes('pet')) {
                isInSelectedCategory = selectedCat.includes('pet') && productCategory.includes('pet');
              }
              // Handle variations like "Home & Kitchen" vs "Home and Kitchen"
              else if (selectedCat.includes('home') || productCategory.includes('home')) {
                isInSelectedCategory = selectedCat.includes('home') && productCategory.includes('home');
              }
              // Handle variations like "Personal Care" vs "Personal"
              else if (selectedCat.includes('personal') || productCategory.includes('personal')) {
                isInSelectedCategory = selectedCat.includes('personal') && productCategory.includes('personal');
              }
              // Handle variations like "Electronics" vs "Electronic"
              else if (selectedCat.includes('electronic') || productCategory.includes('electronic')) {
                isInSelectedCategory = selectedCat.includes('electronic') && productCategory.includes('electronic');
              }
            }
          }
          
          return hasPriceInCurrency && isInSelectedCategory;
        }).length;
        
        if (currentFeaturedCount >= 4) {
          showNotification(`Maximum 4 products allowed in Featured Category for ${selectedCurrency} ${selectedCategoryFilter}`, 'error');
          return;
        }
      }
      
      const { error } = await supabase
        .from('products')
        .update({ is_featured_category: !currentPinStatus })
        .eq('id', product.id);

      if (error) {
        console.error('Error toggling featured category status:', error);
        showNotification('Failed to update featured category status', 'error');
      } else {
        setProducts(products.map(p =>
          p.id === product.id
            ? { ...p, is_featured_category: !currentPinStatus }
            : p
        ));
        showNotification(
          currentPinStatus
            ? 'Product removed from featured category successfully'
            : 'Product added to featured category successfully',
          'success'
        );
      }
    } catch (error) {
      console.error('Error toggling featured category status:', error);
      showNotification('Failed to update featured category status', 'error');
    }
  };

  const handleToggleHotDeal = async (product, section) => {
    try {
      let filterField = '';
      let currentStatus = false;
      
      switch (section) {
        case 'hot_products':
          filterField = 'is_hot_product';
          currentStatus = product.is_hot_product || false;
          break;
        case 'todays_deals':
          filterField = 'is_todays_deal';
          currentStatus = product.is_todays_deal || false;
          break;
        case 'new_arrivals':
          filterField = 'is_new_arrival';
          currentStatus = product.is_new_arrival || false;
          break;
        case 'exclusive_offers':
          filterField = 'is_exclusive_offer';
          currentStatus = product.is_exclusive_offer || false;
          break;
        default:
          return;
      }

      // If trying to add a product that's already in this section, show error
      if (!currentStatus && hotDealsProducts[section].find(p => p.id === product.id)) {
        showNotification(`Product is already in ${section.replace('_', ' ')} section`, 'error');
        return;
      }

      // If trying to add a product and section already has 4 products for this currency, show error
      if (!currentStatus) {
        // Count current products in this section for the selected currency
        const currentCurrencyCount = hotDealsProducts[section].filter(p => {
          let hasCurrencyPricing = false;
          switch (selectedCurrency) {
            case 'USD':
              hasCurrencyPricing = p.price_usd && parseFloat(p.price_usd) > 0;
              break;
            case 'GBP':
              hasCurrencyPricing = p.price_gbp && parseFloat(p.price_gbp) > 0;
              break;
            case 'AED':
              hasCurrencyPricing = p.price_aed && parseFloat(p.price_aed) > 0;
              break;
            case 'SAR':
              hasCurrencyPricing = p.price_sar && parseFloat(p.price_sar) > 0;
              break;
            default:
              hasCurrencyPricing = true;
          }
          return hasCurrencyPricing;
        }).length;
        
        if (currentCurrencyCount >= 4) {
          showNotification(`Maximum 4 products allowed in ${section.replace('_', ' ')} section for ${selectedCurrency}`, 'error');
          return;
        }
      }

      const newStatus = !currentStatus;
      
      const { error } = await supabase
        .from('products')
        .update({ [filterField]: newStatus })
        .eq('id', product.id);

      if (error) {
        console.error(`Error updating ${section} status:`, error);
        showNotification(`Failed to update ${section} status`, 'error');
      } else {
        // Update local state
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.id === product.id 
              ? { ...p, [filterField]: newStatus }
              : p
          )
        );
        
        // Refresh hot deals data to ensure UI is in sync
        await loadHotDealsProducts();
        
        showNotification(
          `Product ${newStatus ? 'added to' : 'removed from'} ${section.replace('_', ' ')} successfully`,
          'success'
        );
      }
    } catch (error) {
      console.error(`Error toggling ${section} status:`, error);
      showNotification(`Failed to update ${section} status`, 'error');
    }
  };

  const handleDeleteOrder = async (order) => {
    setDeleteTarget(order);
    setDeleteType('order');
    setShowDeleteModal(true);
  };

  const handleMarkAsDelivered = async (order) => {
    try {
      setLoading(true);
      
      // Update the order status in the database
      const { error } = await supabase
        .from('orders')
        .update({ status: 'delivered' })
        .eq('id', order.id);

      if (error) {
        console.error('Error updating order status:', error);
        showNotification('Failed to mark order as delivered. Please try again.', 'error');
      } else {
        // Update the local state
        setOrders(prevOrders => 
          prevOrders.map(o => 
            o.id === order.id 
              ? { ...o, status: 'delivered' }
              : o
          )
        );
        
        // Update the selected order if it's currently being viewed
        if (selectedOrder && selectedOrder.id === order.id) {
          setSelectedOrder(prev => ({ ...prev, status: 'delivered' }));
        }
        
        showNotification('Order marked as delivered successfully! Customer can now review products.', 'success');
      }
    } catch (error) {
      console.error('Error marking order as delivered:', error);
      showNotification('An error occurred while marking the order as delivered.', 'error');
    } finally {
      setLoading(false);
    }
  };



  const handleSaveProduct = async () => {
    try {
      // Prepare the update data with proper numeric conversion
      const updateData = {
        name: productFormData.name,
        description: productFormData.description,
        price_usd: productFormData.price_usd && productFormData.price_usd !== '' ? parseFloat(productFormData.price_usd) : null,
        price_gbp: productFormData.price_gbp && productFormData.price_gbp !== '' ? parseFloat(productFormData.price_gbp) : null,
        price_aed: productFormData.price_aed && productFormData.price_aed !== '' ? parseFloat(productFormData.price_aed) : null,
        price_sar: productFormData.price_sar && productFormData.price_sar !== '' ? parseFloat(productFormData.price_sar) : null,
        stock: productFormData.stock && productFormData.stock !== '' ? parseInt(productFormData.stock) : null,
        category: productFormData.category,
        status: productFormData.status,
        image_url: productFormData.image_url
      };

      // Only add variations data if it exists and is properly set
      if (productFormData.has_variations !== undefined) {
        updateData.has_variations = productFormData.has_variations;
      }
      
      if (variations && variations.length > 0) {
        updateData.variations = variations;
      } else if (productFormData.has_variations === false) {
        updateData.variations = [];
      }

      console.log('Updating product with data:', updateData);
      console.log('Product ID:', editingProduct.id);
      console.log('Variations data:', variations);
      console.log('Has variations:', productFormData.has_variations);

      const { error } = await supabase
        .from('products')
        .update(updateData)
        .eq('id', editingProduct.id);

      if (error) {
        console.error('Detailed error updating product:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        showNotification(`Failed to update product: ${error.message}`, 'error');
      } else {
        showNotification('Product updated successfully!', 'success');
        closeProductModal();
        loadAdminData(); // Refresh the data
      }
    } catch (error) {
      console.error('Exception updating product:', error);
      showNotification(`Failed to update product: ${error.message}`, 'error');
    }
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setEditingProduct(null);
    setProductFormData({
      name: '',
      description: '',
      price_usd: '',
      price_gbp: '',
      price_aed: '',
      price_sar: '',
      stock: '',
      category: '',
      status: 'active',
      image_url: ''
    });
  };

  const handleProductInputChange = (e) => {
    const { name, value } = e.target;
    setProductFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Admin Products management functions
  const handleAddAdminProduct = () => {
    setEditingAdminProduct(null);
    setAdminProductFormData({
      name: '',
      description: '',
      price_usd: '',
      price_gbp: '',
      price_aed: '',
      price_sar: '',
      stock: '',
      category: '',
      status: 'active',
      image_url: '',
      vendor_id: null
    });
    setShowAdminProductModal(true);
  };

  const handleEditAdminProduct = (product) => {
    setEditingAdminProduct({ ...product });
    setAddProductFormData({
      name: product.name || '',
      description: product.description || '',
      long_description: product.long_description || '',
      price_usd: product.price_usd || '',
      price_gbp: product.price_gbp || '',
      price_aed: product.price_aed || '',
      price_sar: product.price_sar || '',
      original_price_usd: product.original_price_usd || '',
      original_price_gbp: product.original_price_gbp || '',
      original_price_aed: product.original_price_aed || '',
      original_price_sar: product.original_price_sar || '',
      stock: product.stock || '',
      category: product.category || '',
      subcategories: product.subcategory ? (() => {
        try {
          return typeof product.subcategory === 'string' ? JSON.parse(product.subcategory) : [product.subcategory];
        } catch (e) {
          console.warn('Error parsing subcategory JSON:', e);
          return [product.subcategory];
        }
      })() : [],
      newSubcategory: '',
      brand: product.brand || '',
      sku: product.sku || '',
      image_url: product.image_url || '',
      gallery_images: product.gallery_images || [],
      video_url: product.video_url || '',
      video_thumbnail: product.video_thumbnail || '',
      specifications: product.specifications || {},
      features: product.features || [],
      tags: product.tags || [],
      weight: product.weight || '',
      weight_unit: product.weight_unit || 'g',
      dimensions: product.dimensions || '',
      dimension_unit: product.dimension_unit || 'cm',
      countries: product.countries || [],
      color: product.color || '',
      material: product.material || '',
      warranty: product.warranty || '',
      shipping_info: product.shipping_info || '',
      return_policy: product.return_policy || '',
      is_featured: product.is_featured || false,
      is_bestseller: product.is_bestseller || false,
      is_new_arrival: product.is_new_arrival || false,
      meta_title: product.meta_title || '',
      meta_description: product.meta_description || '',
      seo_keywords: product.seo_keywords || '',
      has_variations: product.has_variations || false,
      variations: product.variations || []
    });
    setActiveSection('add-product');
  };

  const handleDeleteAdminProduct = async (product) => {
    setDeleteTarget(product);
    setDeleteType('admin-product');
    setShowDeleteModal(true);
  };

  const handleSaveAdminProduct = async () => {
    try {
      const productData = {
        name: adminProductFormData.name,
        description: adminProductFormData.description,
        long_description: adminProductFormData.long_description,
        price_usd: parseFloat(adminProductFormData.price_usd) || 0,
        price_gbp: parseFloat(adminProductFormData.price_gbp) || 0,
        price_aed: parseFloat(adminProductFormData.price_aed) || 0,
        price_sar: parseFloat(adminProductFormData.price_sar) || 0,
        original_price_usd: parseFloat(adminProductFormData.original_price_usd) || 0,
        original_price_gbp: parseFloat(adminProductFormData.original_price_gbp) || 0,
        original_price_aed: parseFloat(adminProductFormData.original_price_aed) || 0,
        original_price_sar: parseFloat(adminProductFormData.original_price_sar) || 0,
        stock: parseInt(adminProductFormData.stock) || 0,
        category: adminProductFormData.category,
        subcategory: adminProductFormData.subcategory,
        brand: adminProductFormData.brand,
        sku: adminProductFormData.sku,
        status: adminProductFormData.status,
        image_url: adminProductFormData.image_url,
        video_url: adminProductFormData.video_url,
        weight: adminProductFormData.weight,
        weight_unit: adminProductFormData.weight_unit,
        dimensions: adminProductFormData.dimensions,
        dimension_unit: adminProductFormData.dimension_unit,
        countries: adminProductFormData.countries,
        color: adminProductFormData.color,
        material: adminProductFormData.material,
        warranty: adminProductFormData.warranty,
        shipping_info: adminProductFormData.shipping_info,
        return_policy: adminProductFormData.return_policy,
        is_featured: adminProductFormData.is_featured,
        is_bestseller: adminProductFormData.is_bestseller,
        is_new_arrival: adminProductFormData.is_new_arrival,
        meta_title: adminProductFormData.meta_title,
        meta_description: adminProductFormData.meta_description,
        seo_keywords: adminProductFormData.seo_keywords,
        vendor_id: null // Admin products have no vendor_id
      };

      if (editingAdminProduct) {
        // Update existing admin product
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingAdminProduct.id);

        if (error) {
          console.error('Error updating admin product:', error);
          showNotification('Failed to update admin product. Please try again.', 'error');
        } else {
          showNotification('Admin product updated successfully!', 'success');
          closeAdminProductModal();
          loadAdminData(); // Refresh the data
        }
      } else {
        // Add new admin product
        const { error } = await supabase
          .from('products')
          .insert([productData])
          .select()
          .single();

        if (error) {
          console.error('Error adding admin product:', error);
          showNotification('Failed to add admin product. Please try again.', 'error');
        } else {
          showNotification('Admin product added successfully!', 'success');
          closeAdminProductModal();
          loadAdminData(); // Refresh the data
        }
      }
    } catch (error) {
      console.error('Error saving admin product:', error);
      showNotification('Failed to save admin product. Please try again.', 'error');
    }
  };

  const closeAdminProductModal = () => {
    setShowAdminProductModal(false);
    setEditingAdminProduct(null);
    setAdminProductFormData({
      name: '',
      description: '',
      long_description: '',
      price_usd: '',
      price_gbp: '',
      price_aed: '',
      price_sar: '',
      original_price_usd: '',
      original_price_gbp: '',
      original_price_aed: '',
      original_price_sar: '',
      stock: '',
      category: '',
      subcategory: '',
      brand: '',
      sku: '',
      status: 'active',
      image_url: '',
      video_url: '',
      weight: '',
      weight_unit: 'g',
      dimensions: '',
      dimension_unit: 'cm',
      countries: [],
      color: '',
      material: '',
      warranty: '',
      shipping_info: '',
      return_policy: '',
      is_featured: false,
      is_bestseller: false,
      is_new_arrival: false,
      meta_title: '',
      meta_description: '',
      seo_keywords: '',
      vendor_id: null
    });
  };

  const handleAdminProductInputChange = (e) => {
    const { name, value } = e.target;
    setAdminProductFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveAdminProductAsDraft = async () => {
    // For drafts, only name is required - everything else is optional
    if (!adminProductFormData.name || adminProductFormData.name.trim() === '') {
      showNotification('Please provide at least a product name to save as draft.', 'error');
      return;
    }

    try {
      // Helper function to safely parse numeric values
      const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return null;
        }
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return null;
        }
        return parsed;
      };

      // Helper function to safely parse integer values
      const parseIntValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return 0;
        }
        const parsed = parseInt(value);
        if (isNaN(parsed)) {
          return 0;
        }
        return parsed;
      };

      const productData = {
        name: adminProductFormData.name.trim(),
        description: adminProductFormData.description || '',
        price_usd: parseNumericValue(adminProductFormData.price_usd),
        price_gbp: parseNumericValue(adminProductFormData.price_gbp),
        price_aed: parseNumericValue(adminProductFormData.price_aed),
        price_sar: parseNumericValue(adminProductFormData.price_sar),
        stock: parseIntValue(adminProductFormData.stock),
        category: adminProductFormData.category || '',
        status: 'draft',
        image_url: adminProductFormData.image_url || '',
        vendor_id: null // Admin products have no vendor_id
      };

      if (editingAdminProduct) {
        // Update existing admin product as draft
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingAdminProduct.id);

        if (error) {
          console.error('Error updating admin product as draft:', error);
          showNotification('Failed to update admin product as draft. Please try again.', 'error');
        } else {
          showNotification('Admin product saved as draft successfully!', 'success');
          closeAdminProductModal();
          loadAdminData(); // Refresh the data
        }
      } else {
        // Add new admin product as draft
        const { error } = await supabase
          .from('products')
          .insert([productData])
          .select()
          .single();

        if (error) {
          console.error('Error adding admin product as draft:', error);
          showNotification('Failed to add admin product as draft. Please try again.', 'error');
        } else {
          showNotification('Admin product saved as draft successfully!', 'success');
          closeAdminProductModal();
          loadAdminData(); // Refresh the data
        }
      }
    } catch (error) {
      console.error('Error saving admin product as draft:', error);
      showNotification('Failed to save admin product as draft. Please try again.', 'error');
    }
  };

  // Add Product functions
  const handleAddProductInputChange = (e) => {
    const { name, value } = e.target;
    setAddProductFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Load draft when entering add product section
  const loadDraftIfExists = () => {
    const draftData = draftSaver.loadDraft();
    if (draftData) {
      setAddProductFormData(draftData);
      setHasDraft(true);
      setDraftInfo(draftSaver.getDraftInfo());
      showNotification('Draft loaded successfully!', 'success');
    }
  };

  // Save draft manually
  const saveDraftManually = () => {
    const success = draftSaver.saveDraft(addProductFormData);
    if (success) {
      setHasDraft(true);
      setDraftInfo(draftSaver.getDraftInfo());
      showNotification('Draft saved successfully!', 'success');
    } else {
      showNotification('Failed to save draft', 'error');
    }
  };

  // Clear draft
  const clearDraft = () => {
    const success = draftSaver.clearDraft();
    if (success) {
      setHasDraft(false);
      setDraftInfo(null);
      showNotification('Draft cleared successfully!', 'success');
    }
  };

  // Helper functions for add product form
  const handleImageUpload = async (file) => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `product-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setAddProductFormData(prev => ({ ...prev, image_url: publicUrl }));
      showNotification('Image uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      showNotification('Error uploading image. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (file) => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `product-videos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-videos')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('product-videos')
        .getPublicUrl(filePath);

      setAddProductFormData(prev => ({ ...prev, video_url: publicUrl }));
      showNotification('Video uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading video:', error);
      showNotification('Error uploading video. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryImageUpload = async (files) => {
    try {
      setUploading(true);
      const uploadedUrls = [];

      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `product-images/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(filePath, file);

        if (uploadError) {
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('product-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setGalleryImages(prev => [...prev, ...uploadedUrls]);
      setAddProductFormData(prev => ({ 
        ...prev, 
        gallery_images: [...prev.gallery_images, ...uploadedUrls] 
      }));
      showNotification('Gallery images uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      showNotification('Error uploading gallery images. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
    setAddProductFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const addSpecification = () => {
    setSpecifications(prev => [...prev, { key: '', value: '' }]);
  };

  const removeSpecification = (index) => {
    setSpecifications(prev => prev.filter((_, i) => i !== index));
  };

  const updateSpecification = (index, field, value) => {
    setSpecifications(prev => prev.map((spec, i) => 
      i === index ? { ...spec, [field]: value } : spec
    ));
  };

  const addFeature = () => {
    setFeatures(prev => [...prev, '']);
  };

  const removeFeature = (index) => {
    setFeatures(prev => prev.filter((_, i) => i !== index));
  };

  const updateFeature = (index, value) => {
    setFeatures(prev => prev.map((feature, i) => i === index ? value : feature));
  };

  const addTag = () => {
    setTags(prev => [...prev, '']);
  };

  const removeTag = (index) => {
    setTags(prev => prev.filter((_, i) => i !== index));
  };

  const updateTag = (index, value) => {
    setTags(prev => prev.map((tag, i) => i === index ? value : tag));
  };

  const addVariationType = () => {
    setVariationTypes(prev => [...prev, { name: '', options: [''] }]);
  };

  const removeVariationType = (index) => {
    setVariationTypes(prev => prev.filter((_, i) => i !== index));
  };

  const updateVariationType = (index, field, value) => {
    setVariationTypes(prev => prev.map((type, i) => 
      i === index ? { ...type, [field]: value } : type
    ));
  };

  const addVariationOption = (typeIndex) => {
    setVariationTypes(prev => prev.map((type, i) => 
      i === typeIndex ? { ...type, options: [...type.options, ''] } : type
    ));
  };

  const removeVariationOption = (typeIndex, optionIndex) => {
    setVariationTypes(prev => prev.map((type, i) => 
      i === typeIndex ? { ...type, options: type.options.filter((_, j) => j !== optionIndex) } : type
    ));
  };

  const updateVariationOption = (typeIndex, optionIndex, value) => {
    setVariationTypes(prev => prev.map((type, i) => 
      i === typeIndex ? { ...type, options: type.options.map((option, j) => j === optionIndex ? value : option) } : type
    ));
  };

  const generateVariations = (types) => {
    const generateCombinations = (arrays) => {
      if (arrays.length === 0) return [[]];
      const [first, ...rest] = arrays;
      const restCombinations = generateCombinations(rest);
      return first.flatMap(item => restCombinations.map(combination => [item, ...combination]));
    };

    const optionArrays = types.map(type => type.options.filter(option => option.trim() !== ''));
    const combinations = generateCombinations(optionArrays);
    
    const variations = combinations.map(combination => {
      const name = combination.join(' - ');
      return {
        name,
        price: '',
        original_price: '',
        stock: '',
        sku: '',
        weight: '',
        weight_unit: 'g',
        image_url: '',
        is_active: true
      };
    });

    setVariations(variations);
    setAddProductFormData(prev => ({ ...prev, variations }));
  };

  const updateVariation = (index, field, value) => {
    setVariations(prev => prev.map((variation, i) => 
      i === index ? { ...variation, [field]: value } : variation
    ));
  };

  const handleVariationImageUpload = async (file, variationIndex) => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `product-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setVariations(prev => prev.map((variation, i) => 
        i === variationIndex ? { ...variation, image_url: publicUrl } : variation
      ));
      showNotification('Variation image uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading variation image:', error);
      showNotification('Error uploading variation image. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveAddProduct = async (e) => {
    e.preventDefault();
    
    // Check if at least one price is provided
    const hasPrice = addProductFormData.price_usd || addProductFormData.price_gbp || addProductFormData.price_aed || addProductFormData.price_sar;
    
    if (!addProductFormData.name || !hasPrice || !addProductFormData.stock) {
      showNotification('Please fill in all required fields (Name, at least one currency price, and Stock are mandatory).', 'error');
      return;
    }

    try {
      // Convert specifications array to object
      const specsObject = {};
      specifications.forEach(spec => {
        if (spec.key && spec.value) {
          specsObject[spec.key] = spec.value;
        }
      });

      // Filter out empty features and tags
      const filteredFeatures = features.filter(feature => feature.trim() !== '');
      const filteredTags = tags.filter(tag => tag.trim() !== '');

      const productData = {
        name: addProductFormData.name,
        price_usd: addProductFormData.price_usd && addProductFormData.price_usd !== '' ? parseFloat(addProductFormData.price_usd) : null,
        price_gbp: addProductFormData.price_gbp && addProductFormData.price_gbp !== '' ? parseFloat(addProductFormData.price_gbp) : null,
        price_aed: addProductFormData.price_aed && addProductFormData.price_aed !== '' ? parseFloat(addProductFormData.price_aed) : null,
        price_sar: addProductFormData.price_sar && addProductFormData.price_sar !== '' ? parseFloat(addProductFormData.price_sar) : null,
        original_price_usd: addProductFormData.original_price_usd && addProductFormData.original_price_usd !== '' ? parseFloat(addProductFormData.original_price_usd) : null,
        original_price_gbp: addProductFormData.original_price_gbp && addProductFormData.original_price_gbp !== '' ? parseFloat(addProductFormData.original_price_gbp) : null,
        original_price_aed: addProductFormData.original_price_aed && addProductFormData.original_price_aed !== '' ? parseFloat(addProductFormData.original_price_aed) : null,
        original_price_sar: addProductFormData.original_price_sar && addProductFormData.original_price_sar !== '' ? parseFloat(addProductFormData.original_price_sar) : null,
        category: addProductFormData.category,
        subcategory: addProductFormData.subcategories && addProductFormData.subcategories.length > 0 ? JSON.stringify(addProductFormData.subcategories) : null,
        brand: addProductFormData.brand,
        stock: addProductFormData.stock && addProductFormData.stock !== '' ? parseInt(addProductFormData.stock) : null,
        sku: addProductFormData.sku,
        description: addProductFormData.description,
        long_description: addProductFormData.long_description,
        image_url: addProductFormData.image_url,
        gallery_images: addProductFormData.gallery_images,
        video_url: addProductFormData.video_url,
        video_thumbnail: addProductFormData.video_thumbnail,
        specifications: specsObject,
        features: filteredFeatures,
        tags: filteredTags,
        weight: addProductFormData.weight && addProductFormData.weight !== '' ? parseFloat(addProductFormData.weight) : null,
        weight_unit: addProductFormData.weight_unit || 'g',
        dimensions: addProductFormData.dimensions,
        dimension_unit: addProductFormData.dimension_unit || 'cm',
        countries: addProductFormData.countries || [],
        color: addProductFormData.color,
        material: addProductFormData.material,
        warranty: addProductFormData.warranty,
        shipping_info: addProductFormData.shipping_info,
        return_policy: addProductFormData.return_policy,
        is_featured: addProductFormData.is_featured,
        is_bestseller: addProductFormData.is_bestseller,
        is_new_arrival: addProductFormData.is_new_arrival,
        meta_title: addProductFormData.meta_title,
        meta_description: addProductFormData.meta_description,
        seo_keywords: addProductFormData.seo_keywords,
        has_variations: addProductFormData.has_variations,
        variations: addProductFormData.has_variations ? variations.map(variation => ({
          ...variation,
          price: variation.price && variation.price !== '' ? parseFloat(variation.price) : null,
          original_price: variation.original_price && variation.original_price !== '' ? parseFloat(variation.original_price) : null,
          stock: variation.stock && variation.stock !== '' ? parseInt(variation.stock) : null,
          weight: variation.weight && variation.weight !== '' ? parseFloat(variation.weight) : null
        })) : [],
        vendor_id: null, // Admin products have no vendor_id
        status: editingAdminProduct ? editingAdminProduct.status : 'active'
      };

      let result;
      if (editingAdminProduct) {
        // Update existing product
        const { data, error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingAdminProduct.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
        showNotification('Product updated successfully!', 'success');
      } else {
        // Add new product
        const { data, error } = await supabase
          .from('products')
          .insert([productData])
          .select()
          .single();
        
        if (error) throw error;
        result = data;
        showNotification('Product added successfully!', 'success');
      }

      // Reset form and go back to products
      setAddProductFormData({
        name: '',
        price_usd: '',
        price_gbp: '',
        price_aed: '',
        price_sar: '',
        original_price_usd: '',
        original_price_gbp: '',
        original_price_aed: '',
        original_price_sar: '',
        category: '',
        brand: '',
        stock: '',
        sku: '',
        description: '',
        long_description: '',
        image_url: '',
        gallery_images: [],
        video_url: '',
        video_thumbnail: '',
        specifications: {},
        features: [],
        tags: [],
        weight: '',
        weight_unit: 'g',
        dimensions: '',
        dimension_unit: 'cm',
        countries: [],
        color: '',
        material: '',
        warranty: '',
        shipping_info: '',
        return_policy: '',
        is_featured: false,
        is_bestseller: false,
        is_new_arrival: false,
        meta_title: '',
        meta_description: '',
        seo_keywords: '',
        has_variations: false,
        variations: [],
        subcategories: [],
        newSubcategory: ''
      });
      setGalleryImages([]);
      setSpecifications([{ key: '', value: '' }]);
      setFeatures(['']);
      setTags(['']);
              setVariations([]);
        setVariationTypes([]);
        setEditingAdminProduct(null);
        setActiveSection('products');
      
      // Clear draft after successful submission
      draftSaver.clearDraft();
      setHasDraft(false);
      setDraftInfo(null);
      
      loadAdminData(); // Reload products
      
    } catch (error) {
      console.error('Error saving product:', error);
      showNotification(`Error ${editingProduct ? 'updating' : 'adding'} product. Please try again.`, 'error');
    }
  };

  const handleSaveAsDraft = async () => {
    // For drafts, only name is required - everything else is optional
    if (!addProductFormData.name || addProductFormData.name.trim() === '') {
      showNotification('Please provide at least a product name to save as draft.', 'error');
      return;
    }

    try {
      // Convert specifications array to object
      const specsObject = {};
      specifications.forEach(spec => {
        if (spec.key && spec.value) {
          specsObject[spec.key] = spec.value;
        }
      });

      // Filter out empty features and tags
      const filteredFeatures = features.filter(feature => feature.trim() !== '');
      const filteredTags = tags.filter(tag => tag.trim() !== '');

      // Helper function to safely parse numeric values
      const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return null;
        }
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return null;
        }
        return parsed;
      };

      // Helper function to safely parse integer values
      const parseIntValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return 0;
        }
        const parsed = parseInt(value);
        if (isNaN(parsed)) {
          return 0;
        }
        return parsed;
      };

      const { data, error } = await supabase
        .from('products')
        .insert([
          {
            name: addProductFormData.name.trim(),
            price_usd: parseNumericValue(addProductFormData.price_usd),
            price_gbp: parseNumericValue(addProductFormData.price_gbp),
            price_aed: parseNumericValue(addProductFormData.price_aed),
            price_sar: parseNumericValue(addProductFormData.price_sar),
            original_price_usd: parseNumericValue(addProductFormData.original_price_usd),
            original_price_gbp: parseNumericValue(addProductFormData.original_price_gbp),
            original_price_aed: parseNumericValue(addProductFormData.original_price_aed),
            original_price_sar: parseNumericValue(addProductFormData.original_price_sar),
            category: addProductFormData.category || '',
            brand: addProductFormData.brand || '',
            stock: parseIntValue(addProductFormData.stock),
            sku: addProductFormData.sku || '',
            description: addProductFormData.description || '',
            long_description: addProductFormData.long_description || '',
            image_url: addProductFormData.image_url || '',
            gallery_images: addProductFormData.gallery_images || [],
            video_url: addProductFormData.video_url || '',
            video_thumbnail: addProductFormData.video_thumbnail || '',
            specifications: specsObject,
            features: filteredFeatures,
            tags: filteredTags,
            weight: parseNumericValue(addProductFormData.weight),
            weight_unit: addProductFormData.weight_unit || 'g',
            dimensions: addProductFormData.dimensions || '',
            dimension_unit: addProductFormData.dimension_unit || 'cm',
            countries: addProductFormData.countries || [],
            color: addProductFormData.color || '',
            material: addProductFormData.material || '',
            warranty: addProductFormData.warranty || '',
            shipping_info: addProductFormData.shipping_info || '',
            return_policy: addProductFormData.return_policy || '',
            is_featured: addProductFormData.is_featured || false,
            is_bestseller: addProductFormData.is_bestseller || false,
            is_new_arrival: addProductFormData.is_new_arrival || false,
            meta_title: addProductFormData.meta_title || '',
            meta_description: addProductFormData.meta_description || '',
            seo_keywords: addProductFormData.seo_keywords || '',
            has_variations: addProductFormData.has_variations || false,
            variations: addProductFormData.has_variations ? variations : [],
            subcategory: addProductFormData.subcategories && addProductFormData.subcategories.length > 0 ? JSON.stringify(addProductFormData.subcategories) : null,
            vendor_id: null, // Admin products have no vendor_id
            status: 'draft'
          }
        ])
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Subcategories are now stored directly in the product data
      // No need for separate table relationships

      showNotification('Product saved as draft successfully!', 'success');
      // Reset form
      setAddProductFormData({
        name: '',
        price_usd: '',
        price_gbp: '',
        price_aed: '',
        price_sar: '',
        original_price_usd: '',
        original_price_gbp: '',
        original_price_aed: '',
        original_price_sar: '',
        category: '',
        brand: '',
        stock: '',
        sku: '',
        description: '',
        long_description: '',
        image_url: '',
        gallery_images: [],
        video_url: '',
        video_thumbnail: '',
        specifications: {},
        features: [],
        tags: [],
        weight: '',
        weight_unit: 'g',
        dimensions: '',
        dimension_unit: 'cm',
        countries: [],
        color: '',
        material: '',
        warranty: '',
        shipping_info: '',
        return_policy: '',
        is_featured: false,
        is_bestseller: false,
        is_new_arrival: false,
        meta_title: '',
        meta_description: '',
        seo_keywords: '',
        has_variations: false,
        variations: [],
        subcategories: [],
        newSubcategory: ''
      });
      setGalleryImages([]);
      setSpecifications([{ key: '', value: '' }]);
      setFeatures(['']);
      setTags(['']);
      setVariations([]);
      setVariationTypes([]);
      
      setActiveSection('drafts');
      setEditingAdminProduct(null);
      loadAdminData(); // Reload products
      
    } catch (error) {
      console.error('Error saving product as draft:', error);
      showNotification('Error saving product as draft. Please try again.', 'error');
    }
  };

  // Validation function to check if a draft product has all mandatory fields
  const isDraftReadyToPublish = (product) => {
    // Check if product name exists and is not empty
    if (!product.name || product.name.trim() === '') {
      return false;
    }

    // Check if at least one price is provided
    const hasPrice = product.price_usd || product.price_gbp || product.price_aed || product.price_sar;
    if (!hasPrice) {
      return false;
    }

    // Check if stock is provided and is greater than 0
    if (!product.stock || product.stock <= 0) {
      return false;
    }

    // Check if category is provided
    if (!product.category || product.category.trim() === '') {
      return false;
    }

    // Check if description is provided
    if (!product.description || product.description.trim() === '') {
      return false;
    }

    // Check if image is provided
    if (!product.image_url || product.image_url.trim() === '') {
      return false;
    }

    return true;
  };

  const handlePublishDraft = async (product) => {
    // Validate the product before publishing
    if (!isDraftReadyToPublish(product)) {
      showNotification('Cannot publish draft. Please fill in all mandatory fields: Name, Price, Stock, Category, Description, and Image.', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('products')
        .update({ status: 'active' })
        .eq('id', product.id);

      if (error) {
        throw error;
      }

      showNotification('Product published successfully!', 'success');
      loadAdminData(); // Reload products
    } catch (error) {
      console.error('Error publishing product:', error);
      showNotification('Error publishing product. Please try again.', 'error');
    }
  };

  // Category management functions
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      description: '',
      status: 'active'
    });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory({ ...category });
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      status: category.status || 'active'
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (category) => {
    setDeleteTarget(category);
    setDeleteType('category');
    setShowDeleteModal(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        // Update existing category
        const { error } = await supabase
          .from('categories')
          .update({
            name: categoryFormData.name,
            description: categoryFormData.description,
            status: categoryFormData.status
          })
          .eq('id', editingCategory.id);

        if (error) {
          console.error('Error updating category:', error);
          showNotification('Failed to update category. Please try again.', 'error');
        } else {
          showNotification('Category updated successfully!', 'success');
          closeCategoryModal();
          loadAdminData(); // Refresh the data
        }
      } else {
        // Add new category
        const { error } = await supabase
          .from('categories')
          .insert([{
            name: categoryFormData.name,
            description: categoryFormData.description,
            status: categoryFormData.status
          }])
          .select()
          .single();

        if (error) {
          console.error('Error adding category:', error);
          showNotification('Failed to add category. Please try again.', 'error');
        } else {
          showNotification('Category added successfully!', 'success');
          closeCategoryModal();
          loadAdminData(); // Refresh the data
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showNotification('Failed to save category. Please try again.', 'error');
    }
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      description: '',
      status: 'active'
    });
  };

  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Subcategory management functions
  const handleAddSubcategory = () => {
    setShowSubcategoryModal(true);
    setEditingSubcategory(null);
    setSubcategoryFormData({
      name: '',
      description: '',
      category_id: '',
      status: 'active'
    });
  };

  const handleEditSubcategory = (subcategory) => {
    setShowSubcategoryModal(true);
    setEditingSubcategory(subcategory);
    setSubcategoryFormData({
      name: subcategory.name,
      description: subcategory.description,
      category_id: subcategory.category_id,
      status: subcategory.status
    });
  };

  const handleDeleteSubcategory = async (subcategory) => {
    setDeleteTarget(subcategory);
    setDeleteType('subcategory');
    setShowDeleteModal(true);
  };

  const handleSaveSubcategory = async () => {
    try {
      if (!subcategoryFormData.name.trim()) {
        showNotification('Subcategory name is required.', 'error');
        return;
      }

      if (!subcategoryFormData.category_id) {
        showNotification('Please select a parent category.', 'error');
        return;
      }

      if (editingSubcategory) {
        // Update existing subcategory
        const { error } = await supabase
          .from('subcategories')
          .update({
            name: subcategoryFormData.name.trim(),
            description: subcategoryFormData.description.trim(),
            category_id: subcategoryFormData.category_id,
            status: subcategoryFormData.status
          })
          .eq('id', editingSubcategory.id);

        if (error) {
          console.error('Error updating subcategory:', error);
          showNotification('Failed to update subcategory. Please try again.', 'error');
        } else {
          showNotification('Subcategory updated successfully!', 'success');
          closeSubcategoryModal();
          loadAdminData(); // Refresh the data
        }
      } else {
        // Add new subcategory
        const { error } = await supabase
          .from('subcategories')
          .insert([{
            name: subcategoryFormData.name.trim(),
            description: subcategoryFormData.description.trim(),
            category_id: subcategoryFormData.category_id,
            status: subcategoryFormData.status
          }])
          .select()
          .single();

        if (error) {
          console.error('Error adding subcategory:', error);
          showNotification('Failed to add subcategory. Please try again.', 'error');
        } else {
          showNotification('Subcategory added successfully!', 'success');
          closeSubcategoryModal();
          loadAdminData(); // Refresh the data
        }
      }
    } catch (error) {
      console.error('Error saving subcategory:', error);
      showNotification('Failed to save subcategory. Please try again.', 'error');
    }
  };

  const closeSubcategoryModal = () => {
    setShowSubcategoryModal(false);
    setEditingSubcategory(null);
    setSubcategoryFormData({
      name: '',
      description: '',
      category_id: '',
      status: 'active'
    });
  };

  const handleSubcategoryInputChange = (e) => {
    const { name, value } = e.target;
    setSubcategoryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApproveVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorAction('approve');
    setShowVendorModal(true);
  };

  const handleRejectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorAction('reject');
    setShowVendorModal(true);
  };

  const confirmVendorAction = async () => {
    if (!selectedVendor) return;

    try {
      let actionText;
      
      if (vendorAction === 'reject') {
        // Delete the vendor from database when rejected
        const { error } = await supabase
          .from('vendors')
          .delete()
          .eq('id', selectedVendor.id);

        if (error) {
          console.error('Error deleting vendor:', error);
          showNotification('Failed to reject vendor. Please try again.', 'error');
        } else {
          actionText = 'rejected and removed from database';
          showNotification(`Vendor ${actionText} successfully!`, 'success');
          
          // Send email notification to vendor (optional)
          try {
            const { sendVendorStatusNotification } = await import('../utils/emailNotification.js');
            await sendVendorStatusNotification(selectedVendor, 'rejected');
          } catch (emailError) {
            console.error('Email notification error:', emailError);
          }
        }
      } else {
        // Update status for approve/suspend/reactivate
        let newStatus;
        
        switch (vendorAction) {
          case 'approve':
            newStatus = 'active';
            actionText = 'approved';
            break;
          case 'suspend':
            newStatus = 'suspended';
            actionText = 'suspended';
            break;
          case 'reactivate':
            newStatus = 'active';
            actionText = 'reactivated';
            break;
          default:
            newStatus = 'active';
            actionText = 'updated';
        }

        const { error } = await supabase
          .from('vendors')
          .update({ status: newStatus })
          .eq('id', selectedVendor.id);

        if (error) {
          console.error(`Error ${vendorAction}ing vendor:`, error);
          showNotification(`Failed to ${vendorAction} vendor. Please try again.`, 'error');
        } else {
          showNotification(`Vendor ${actionText} successfully!`, 'success');
          
          // Send email notification to vendor
          try {
            const { sendVendorStatusNotification } = await import('../utils/emailNotification.js');
            await sendVendorStatusNotification(selectedVendor, newStatus);
          } catch (emailError) {
            console.error('Email notification error:', emailError);
          }
        }
      }
      
      loadAdminData(); // Refresh the data
    } catch (error) {
      console.error('Error:', error);
      showNotification('An error occurred. Please try again.', 'error');
    } finally {
      setShowVendorModal(false);
      setSelectedVendor(null);
      setVendorAction('');
    }
  };

  const handleViewVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorAction('view');
    setShowVendorModal(true);
  };

  const handleSuspendVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorAction('suspend');
    setShowVendorModal(true);
  };

  const handleReactivateVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorAction('reactivate');
    setShowVendorModal(true);
  };

  const closeVendorModal = () => {
    setShowVendorModal(false);
    setSelectedVendor(null);
    setVendorAction('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      let error;
      
      console.log('Deleting:', deleteType, 'with ID:', deleteTarget.id);
      
      switch (deleteType) {
        case 'product':
          const { error: productError } = await supabase
            .from('products')
            .delete()
            .eq('id', deleteTarget.id);
          error = productError;
          break;
          
        case 'admin-product':
          const { error: adminProductError } = await supabase
            .from('products')
            .delete()
            .eq('id', deleteTarget.id);
          error = adminProductError;
          break;
          
        case 'order':
          const { error: orderError } = await supabase
            .from('orders')
            .delete()
            .eq('id', deleteTarget.id);
          error = orderError;
          break;
          
        case 'category':
          const { error: categoryError } = await supabase
            .from('categories')
            .delete()
            .eq('id', deleteTarget.id);
          error = categoryError;
          break;
          
        case 'subcategory':
          const { error: subcategoryError } = await supabase
            .from('subcategories')
            .delete()
            .eq('id', deleteTarget.id);
          error = subcategoryError;
          break;
          
        case 'shipping':
          const { error: shippingError } = await supabase
            .from('shipping_rates')
            .delete()
            .eq('id', deleteTarget.id);
          error = shippingError;
          break;
          
        case 'user':
          // Delete customer from the customers table
          const { error: customerError } = await supabase
            .from('customers')
            .delete()
            .eq('id', deleteTarget.id);
          error = customerError;
          break;
          
        default:
          error = new Error('Unknown delete type');
      }

      if (error) {
        console.error('Error deleting item:', error);
        showNotification(`Failed to delete ${deleteType}. Please try again.`, 'error');
      } else {
        showNotification(`${deleteType.charAt(0).toUpperCase() + deleteType.slice(1)} deleted successfully!`, 'success');
        loadAdminData(); // Refresh the data
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showNotification(`Failed to delete ${deleteType}. Please try again.`, 'error');
    } finally {
      closeDeleteModal();
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleteType('');
  };

  // Shipping management functions
  const handleAddShipping = () => {
    setEditingShipping(null);
    setShippingFormData({
      country_name: '',
      country_code: '',
      shipping_cost: '',
      estimated_delivery_days: 7,
      is_active: true
    });
    setShowShippingModal(true);
  };

  const handleEditShipping = (shipping) => {
    setEditingShipping({ ...shipping });
    setShippingFormData({
      country_name: shipping.country_name || '',
      country_code: shipping.country_code || '',
      shipping_cost: shipping.shipping_cost || '',
      estimated_delivery_days: shipping.estimated_delivery_days || 7,
      is_active: shipping.is_active !== false
    });
    setShowShippingModal(true);
  };

  const handleDeleteShipping = async (shipping) => {
    setDeleteTarget(shipping);
    setDeleteType('shipping');
    setShowDeleteModal(true);
  };

  const handleSaveShipping = async () => {
    try {
      const shippingData = {
        country_name: shippingFormData.country_name,
        country_code: shippingFormData.country_code,
        shipping_cost: parseFloat(shippingFormData.shipping_cost),
        estimated_delivery_days: parseInt(shippingFormData.estimated_delivery_days),
        is_active: shippingFormData.is_active
      };

      if (editingShipping) {
        // Update existing shipping rate
        const { error } = await supabase
          .from('shipping_rates')
          .update(shippingData)
          .eq('id', editingShipping.id);

        if (error) {
          console.error('Error updating shipping rate:', error);
          showNotification('Failed to update shipping rate. Please try again.', 'error');
        } else {
          showNotification('Shipping rate updated successfully!', 'success');
          closeShippingModal();
          loadAdminData(); // Refresh the data
        }
      } else {
        // Add new shipping rate
        const { error } = await supabase
          .from('shipping_rates')
          .insert([shippingData]);

        if (error) {
          console.error('Error adding shipping rate:', error);
          showNotification('Failed to add shipping rate. Please try again.', 'error');
        } else {
          showNotification('Shipping rate added successfully!', 'success');
          closeShippingModal();
          loadAdminData(); // Refresh the data
        }
      }
    } catch (error) {
      console.error('Error saving shipping rate:', error);
      showNotification('Failed to save shipping rate. Please try again.', 'error');
    }
  };

  const closeShippingModal = () => {
    setShowShippingModal(false);
    setEditingShipping(null);
    setShippingFormData({
      country_name: '',
      country_code: '',
      shipping_cost: '',
      estimated_delivery_days: 7,
      is_active: true
    });
  };

  const handleShippingInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setShippingFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleViewQuery = (query) => {
    setSelectedQuery(query);
    setQueryFormData({
      status: query.status || 'pending',
      admin_notes: '' // Start with empty admin notes for new input
    });
    setShowQueryModal(true);
  };

  const closeQueryModal = () => {
    setSelectedQuery(null);
    setShowQueryModal(false);
    setQueryFormData({ status: '', admin_notes: '' });
  };

  const handleQueryInputChange = (e) => {
    const { name, value } = e.target;
    setQueryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveQuery = async () => {
    if (!selectedQuery) return;

    try {
      // Combine existing notes with new notes
      const existingNotes = selectedQuery.admin_notes || '';
      const newNotes = queryFormData.admin_notes.trim();
      const combinedNotes = newNotes 
        ? (existingNotes ? `${existingNotes}\n\n--- New Note ---\n${newNotes}` : newNotes)
        : existingNotes;

      const { error } = await supabase
        .from('contact_queries')
        .update({
          status: queryFormData.status,
          admin_notes: combinedNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedQuery.id);

      if (error) {
        console.error('Error updating query:', error);
        showNotification('Failed to update query', 'error');
      } else {
        // Update local state
        setContactQueries(prevQueries => 
          prevQueries.map(q => 
            q.id === selectedQuery.id 
              ? { 
                  ...q, 
                  status: queryFormData.status, 
                  admin_notes: combinedNotes,
                  updated_at: new Date().toISOString()
                }
              : q
          )
        );
        
        showNotification('Query updated successfully', 'success');
        closeQueryModal();
      }
    } catch (error) {
      console.error('Error updating query:', error);
      showNotification('Failed to update query', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  const renderOverview = () => (
    <div className="admin-overview">
      <div className="admin-stats">
        <div className="stat-card">
          <div className="stat-icon">🛒</div>
          <div className="stat-info">
            <h3>Total Orders</h3>
            <p className="stat-number">{orders.length}</p>
            <p className="stat-label">All time orders</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-info">
            <h3>Total Products</h3>
            <p className="stat-number">{products.length}</p>
            <p className="stat-label">Active products</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <h3>Total Users</h3>
            <p className="stat-number">{users.length}</p>
            <p className="stat-label">Registered customers</p>
          </div>
        </div>
      </div>

      <div className="admin-sections">
        <div className="admin-section">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button className="action-btn" onClick={() => setActiveSection('orders')}>
              <span className="action-icon">🛒</span>
              View Orders
            </button>
            <button className="action-btn" onClick={() => setActiveSection('products')}>
              <span className="action-icon">📦</span>
              Manage Products
            </button>
            <button className="action-btn" onClick={() => setActiveSection('users')}>
              <span className="action-icon">👥</span>
              Manage Users
            </button>
          </div>
        </div>

        <div className="admin-section">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            {orders.length > 0 && (
              <div className="activity-item">
                <div className="activity-icon">💰</div>
                <div className="activity-content">
                  <p>Total {orders.length} orders processed</p>
                  <span className="activity-time">Latest: ${orders[0]?.total || 0}</span>
                </div>
              </div>
            )}
            {products.length > 0 && (
              <div className="activity-item">
                <div className="activity-icon">📦</div>
                <div className="activity-content">
                  <p>Total {products.length} products available</p>
                  <span className="activity-time">Latest: {products[0]?.name || 'Unknown Product'}</span>
                </div>
              </div>
            )}
            {users.length > 0 && (
              <div className="activity-item">
                <div className="activity-icon">👥</div>
                <div className="activity-content">
                  <p>Total {users.length} customers registered</p>
                  <span className="activity-time">Latest: {users[0]?.name || 'Unknown Customer'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );



  const renderOrders = () => (
    <div className="admin-orders">
      <div className="section-header">
        <h2>All Orders</h2>
        <button 
          className="refresh-btn" 
          onClick={loadAdminData}
          disabled={loading}
          style={{ marginLeft: 'auto', padding: '8px 16px' }}
        >
          {loading ? '🔄 Loading...' : '🔄 Refresh'}
        </button>
      </div>
      
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : orders.length === 0 ? (
        <div className="empty-state" style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📦</div>
          <h3>No Orders Found</h3>
          <p>There are no orders in the database yet.</p>
          <p style={{ fontSize: '14px', marginTop: '10px', color: '#999' }}>
            Orders will appear here once customers complete their purchases.
          </p>
          <button 
            onClick={loadAdminData}
            style={{ 
              marginTop: '20px', 
              padding: '10px 20px',
              backgroundColor: '#232f3e',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            🔄 Refresh Orders
          </button>
        </div>
      ) : (
        <div className="orders-table">
          <div style={{ marginBottom: '10px', color: '#666', fontSize: '14px' }}>
            Showing {orders.length} order{orders.length !== 1 ? 's' : ''}
          </div>
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Total</th>
                <th>Payment Status</th>
                <th>Order Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td>{order.orderNumber}</td>
                  <td>{order.customer}</td>
                  <td>{order.customerEmail || 'N/A'}</td>
                  <td>${order.total}</td>
                  <td>
                    <span className={`status-badge ${order.paymentStatus}`}>
                      {order.paymentStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${order.status}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>{order.date}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="view-btn" onClick={() => handleViewOrder(order)}>View</button>
                      {order.status !== 'delivered' && (
                        <button 
                          className="deliver-btn" 
                          onClick={() => handleMarkAsDelivered(order)}
                          disabled={loading}
                        >
                          {loading ? 'Marking...' : 'Mark Delivered'}
                        </button>
                      )}
                      <button className="delete-btn" onClick={() => handleDeleteOrder(order)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderProducts = () => {
    // Filter products by selected currency
    const filteredProducts = products.filter(product => {
      if (selectedCurrency === 'ALL') {
        return true; // Show all products regardless of currency
      }
      
      let hasCurrencyPricing = false;
      switch (selectedCurrency) {
        case 'USD':
          hasCurrencyPricing = product.price_usd && parseFloat(product.price_usd) > 0;
          break;
        case 'GBP':
          hasCurrencyPricing = product.price_gbp && parseFloat(product.price_gbp) > 0;
          break;
        case 'AED':
          hasCurrencyPricing = product.price_aed && parseFloat(product.price_aed) > 0;
          break;
        case 'SAR':
          hasCurrencyPricing = product.price_sar && parseFloat(product.price_sar) > 0;
          break;
        default:
          hasCurrencyPricing = true;
      }
      return hasCurrencyPricing;
    });

    return (
      <div className="admin-products">
        <div className="section-header">
          <h2>All Products</h2>
          <div className="currency-filter-controls">
            <div className="currency-selector">
              <label htmlFor="currency-filter">Filter by Currency:</label>
              <select 
                id="currency-filter"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="currency-dropdown"
              >
                <option value="USD">USD (US Dollar)</option>
                <option value="GBP">GBP (British Pound)</option>
                <option value="AED">AED (UAE Dirham)</option>
                <option value="SAR">SAR (Saudi Riyal)</option>
                <option value="ALL">All Currencies</option>
              </select>
            </div>
            <p className="currency-info">Products with {selectedCurrency} pricing</p>
          </div>
        </div>
        
        {/* Currency Filter Summary */}
        <div className="currency-filter-summary">
          <span className="filter-info">
            {selectedCurrency === 'ALL' 
              ? 'Showing all products regardless of currency' 
              : `Showing products with ${selectedCurrency} pricing only`
            }
          </span>
          <span className="product-count">
            {filteredProducts.length} products available
          </span>
        </div>
        
        {loading ? (
          <div className="loading">Loading products...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Products Found</h3>
            <p>{selectedCurrency === 'ALL' 
              ? 'No products found.' 
              : `No products found with ${selectedCurrency} pricing.`
            }</p>
          </div>
        ) : (
        <div className="products-grid">
          {filteredProducts.map(product => (
            <div key={product.id} className="product-card">
              <div className="product-image">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} />
                ) : (
                  <span className="product-emoji">📦</span>
                )}
              </div>
              <div className="product-info">
                <h3>{product.name || 'Unnamed Product'}</h3>
                <p className="product-vendor">Vendor: {product.vendor || 'Unknown Vendor'}</p>
                <p className="product-price">
                  {(() => {
                    const { symbol, price } = getPrimaryCurrencyAndPrice(product);
                    return `${symbol}${price.toFixed(2)}`;
                  })()}
                </p>
                <p className="product-stock">Stock: {product.stock || 0}</p>
                <p className="product-category">Category: {product.category || 'Uncategorized'}</p>
                <p className="product-status">
                  <span className={`status-badge ${product.status || 'active'}`}>
                    {product.status || 'Active'}
                  </span>
                </p>
              </div>
              <div className="product-actions">
                <button className="edit-btn" onClick={() => handleEditProduct(product)}>Edit</button>
                <button className="delete-btn" onClick={() => handleDeleteProduct(product)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  const renderDrafts = () => {
    // Filter draft products by selected currency
    const draftProducts = products.filter(product => {
      if (product.status !== 'draft') return false;
      
      if (selectedCurrency === 'ALL') {
        return true; // Show all draft products regardless of currency
      }
      
      let hasCurrencyPricing = false;
      switch (selectedCurrency) {
        case 'USD':
          hasCurrencyPricing = product.price_usd && parseFloat(product.price_usd) > 0;
          break;
        case 'GBP':
          hasCurrencyPricing = product.price_gbp && parseFloat(product.price_gbp) > 0;
          break;
        case 'AED':
          hasCurrencyPricing = product.price_aed && parseFloat(product.price_aed) > 0;
          break;
        case 'SAR':
          hasCurrencyPricing = product.price_sar && parseFloat(product.price_sar) > 0;
          break;
        default:
          hasCurrencyPricing = true;
      }
      return hasCurrencyPricing;
    });
    
    return (
      <div className="admin-drafts">
        <div className="section-header">
          <h2>Draft Products</h2>
          <p>{selectedCurrency === 'ALL' 
            ? 'All draft products' 
            : `Draft products with ${selectedCurrency} pricing`
          }</p>
        </div>
        
        {/* Currency Filter Summary */}
        <div className="currency-filter-summary">
          <span className="filter-info">
            Showing draft products with {selectedCurrency} pricing only
          </span>
          <span className="product-count">
            {draftProducts.length} draft products available
          </span>
        </div>
        
        {loading ? (
          <div className="loading">Loading drafts...</div>
        ) : draftProducts.length === 0 ? (
          <div className="no-drafts">
            <p>{selectedCurrency === 'ALL' 
              ? 'No draft products found.' 
              : `No draft products found with ${selectedCurrency} pricing.`
            }</p>
          </div>
        ) : (
          <div className="products-grid">
            {draftProducts.map(product => {
              const isReadyToPublish = isDraftReadyToPublish(product);
              
              return (
                <div key={product.id} className="product-card draft-card">
                  <div className="product-image">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} />
                    ) : (
                      <span className="product-emoji">📦</span>
                    )}
                    <div className="draft-badge">Draft</div>
                  </div>
                  <div className="product-info">
                    <h3>{product.name || 'Unnamed Product'}</h3>
                    <p className="product-vendor">Vendor: {product.vendor || 'Unknown Vendor'}</p>
                    <p className="product-price">
                      {(() => {
                        const { symbol, price } = getPrimaryCurrencyAndPrice(product);
                        return `${symbol}${price.toFixed(2)}`;
                      })()}
                    </p>
                    <p className="product-stock">Stock: {product.stock || 0}</p>
                    <p className="product-category">Category: {product.category || 'Uncategorized'}</p>
                    <p className="product-status">
                      <span className="status-badge draft">
                        Draft
                      </span>
                    </p>
                    {!isReadyToPublish && (
                      <div className="draft-warning">
                        <p>⚠️ Complete all mandatory fields to publish</p>
                        <ul>
                          {!product.name && <li>• Product name</li>}
                          {!product.price_usd && !product.price_gbp && !product.price_aed && !product.price_sar && <li>• At least one price</li>}
                          {!product.stock && <li>• Stock quantity</li>}
                          {!product.category && <li>• Category</li>}
                          {!product.description && <li>• Description</li>}
                          {!product.image_url && <li>• Product image</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="product-actions">
                    {isReadyToPublish && (
                      <button className="publish-btn" onClick={() => handlePublishDraft(product)}>
                        Publish
                      </button>
                    )}
                    <button className="edit-btn" onClick={() => handleEditProduct(product)}>
                      Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDeleteProduct(product)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderAdminProducts = () => (
    <div className="admin-products">
      <div className="section-header">
        <h2>Admin Products</h2>
        <button className="add-btn" onClick={handleAddAdminProduct}>
          <span>➕</span> Add Admin Product
        </button>
      </div>
      
      {loading ? (
        <div className="loading">Loading admin products...</div>
      ) : (
        <div className="products-grid">
          {adminProducts.length === 0 ? (
            <div className="no-products">
              <p>No admin products found. Click "Add Admin Product" to create your first product.</p>
            </div>
          ) : (
            adminProducts.map(product => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} />
                  ) : (
                    <span className="product-emoji">📦</span>
                  )}
                </div>
                <div className="product-info">
                  <h3>{product.name || 'Unnamed Product'}</h3>
                  <p className="product-vendor">Admin Product</p>
                  <p className="product-price">
                    {(() => {
                      const { symbol, price } = getPrimaryCurrencyAndPrice(product);
                      return `${symbol}${price.toFixed(2)}`;
                    })()}
                  </p>
                  <p className="product-stock">Stock: {product.stock || 0}</p>
                  <p className="product-category">Category: {product.category || 'Uncategorized'}</p>
                  <p className="product-status">
                    <span className={`status-badge ${product.status || 'active'}`}>
                      {product.status || 'Active'}
                    </span>
                  </p>
                </div>
                <div className="product-actions">
                  <button className="edit-btn" onClick={() => handleEditAdminProduct(product)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDeleteAdminProduct(product)}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  const renderAddProduct = () => (
    <div className="dashboard-add-product">
      <div className="section-header">
        <h2>{editingAdminProduct ? 'Edit Product' : 'Add New Product'}</h2>
        <button className="back-btn" onClick={() => {
          setActiveSection('products');
          setEditingAdminProduct(null);
          // Reset form data
          setAddProductFormData({
            name: '',
            price_usd: '',
            price_gbp: '',
            price_aed: '',
            price_sar: '',
            original_price_usd: '',
            original_price_gbp: '',
            original_price_aed: '',
            original_price_sar: '',
            category: '',
            brand: '',
            stock: '',
            sku: '',
            description: '',
            long_description: '',
            image_url: '',
            gallery_images: [],
            video_url: '',
            video_thumbnail: '',
            specifications: {},
            features: [],
            tags: [],
            weight: '',
            weight_unit: 'g',
            dimensions: '',
            dimension_unit: 'cm',
            countries: [],
            color: '',
            material: '',
            warranty: '',
            shipping_info: '',
            return_policy: '',
            is_featured: false,
            is_bestseller: false,
            is_new_arrival: false,
            meta_title: '',
            meta_description: '',
            seo_keywords: '',
            has_variations: false,
            variations: [],
            subcategories: [],
            newSubcategory: ''
          });
          setGalleryImages([]);
          setSpecifications([{ key: '', value: '' }]);
          setFeatures(['']);
          setTags(['']);
          setVariations([]);
          setVariationTypes([]);
        }}>
          ← Back to Products
        </button>
      </div>


      
      <div className="add-product-form">
        <form onSubmit={handleSaveAddProduct}>
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Available Countries</label>
                <div className="countries-section">
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-uae"
                      checked={addProductFormData.countries.includes('UAE')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'UAE'] 
                          }));
                        } else {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'UAE') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-uae">UAE</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-usa"
                      checked={addProductFormData.countries.includes('USA')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'USA'] 
                          }));
                        } else {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'USA') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-usa">USA</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-uk"
                      checked={addProductFormData.countries.includes('UK')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'UK'] 
                          }));
                        } else {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'UK') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-uk">UK</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-saudi"
                      checked={addProductFormData.countries.includes('Saudi Arabia')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'Saudi Arabia'] 
                          }));
                        } else {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'Saudi Arabia') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-saudi">Saudi Arabia</label>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Product Name *</label>
                <input 
                  type="text" 
                  placeholder="Enter product name"
                  value={addProductFormData.name}
                  onChange={(e) => setAddProductFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Brand</label>
                <input 
                  type="text" 
                  placeholder="Enter brand name"
                  value={addProductFormData.brand}
                  onChange={(e) => setAddProductFormData(prev => ({ ...prev, brand: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>SKU</label>
                <input 
                  type="text" 
                  placeholder="Stock Keeping Unit"
                  value={addProductFormData.sku}
                  onChange={(e) => setAddProductFormData(prev => ({ ...prev, sku: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select 
                  value={addProductFormData.category}
                  onChange={(e) => setAddProductFormData(prev => ({ ...prev, category: e.target.value, subcategory: '' }))}
                  required
                  disabled={loading}
                >
                  <option value="">
                    {loading ? 'Loading categories...' : 'Select a category'}
                  </option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subcategories</label>
                <div className="subcategories-container">
                  <div className="subcategories-list">
                    {addProductFormData.subcategories.map((subcategory, index) => (
                      <div key={index} className="subcategory-tag">
                        <span>{subcategory}</span>
                        <button
                          type="button"
                          className="remove-subcategory"
                          onClick={() => {
                            const newSubcategories = addProductFormData.subcategories.filter((_, i) => i !== index);
                            setAddProductFormData(prev => ({ ...prev, subcategories: newSubcategories }));
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="subcategory-input-section">
                    <input
                      type="text"
                      placeholder="Type a subcategory name..."
                      value={addProductFormData.newSubcategory || ''}
                      onChange={(e) => setAddProductFormData(prev => ({ 
                        ...prev, 
                        newSubcategory: e.target.value 
                      }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && addProductFormData.newSubcategory?.trim()) {
                          e.preventDefault();
                          if (!addProductFormData.subcategories.includes(addProductFormData.newSubcategory.trim())) {
                            setAddProductFormData(prev => ({ 
                              ...prev, 
                              subcategories: [...prev.subcategories, prev.newSubcategory.trim()],
                              newSubcategory: ''
                            }));
                          }
                        }
                      }}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="add-subcategory-btn"
                      onClick={() => {
                        if (addProductFormData.newSubcategory?.trim() && 
                            !addProductFormData.subcategories.includes(addProductFormData.newSubcategory.trim())) {
                          setAddProductFormData(prev => ({ 
                            ...prev, 
                            subcategories: [...prev.subcategories, prev.newSubcategory.trim()],
                            newSubcategory: ''
                          }));
                        }
                      }}
                      disabled={!addProductFormData.newSubcategory?.trim() || loading}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="form-section">
            <h3>Pricing & Inventory</h3>
            <div className="form-grid">
              {/* USD Prices - Show only if USA is selected */}
              {addProductFormData.countries.includes('USA') && (
                <>
                  <div className="form-group">
                    <label>Current Price (USD) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.price_usd}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, price_usd: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (USD)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.original_price_usd}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, original_price_usd: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* GBP Prices - Show only if UK is selected */}
              {addProductFormData.countries.includes('UK') && (
                <>
                  <div className="form-group">
                    <label>Current Price (GBP) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.price_gbp}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, price_gbp: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (GBP)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.original_price_gbp}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, original_price_gbp: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* AED Prices - Show only if UAE is selected */}
              {addProductFormData.countries.includes('UAE') && (
                <>
                  <div className="form-group">
                    <label>Current Price (AED) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.price_aed}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, price_aed: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (AED)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.original_price_aed}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, original_price_aed: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* KSA Prices - Show only if Saudi Arabia is selected */}
              {addProductFormData.countries.includes('Saudi Arabia') && (
                <>
                  <div className="form-group">
                    <label>Current Price (KSA) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.price_sar}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, price_sar: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (KSA)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={addProductFormData.original_price_sar}
                      onChange={(e) => setAddProductFormData(prev => ({ ...prev, original_price_sar: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* Stock */}
              <div className="form-group">
                <label>Stock Quantity *</label>
                <input 
                  type="number" 
                  placeholder="0"
                  value={addProductFormData.stock}
                  onChange={(e) => setAddProductFormData(prev => ({ ...prev, stock: e.target.value }))}
                  required
                />
              </div>
            </div>
          </div>

          {/* Descriptions */}
          <div className="form-section">
            <h3>Product Descriptions</h3>
            <div className="form-group">
              <label>Short Description</label>
              <textarea 
                rows="3" 
                placeholder="Brief product description for listings"
                value={addProductFormData.description}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, description: e.target.value }))}
              ></textarea>
            </div>
            <div className="form-group">
              <label>Detailed Description</label>
              <textarea 
                rows="6" 
                placeholder="Comprehensive product description for detail page"
                value={addProductFormData.long_description}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, long_description: e.target.value }))}
              ></textarea>
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3>Product Images</h3>
            <div className="form-group">
              <label>Main Product Image</label>
              <div className="image-upload-section">
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleImageUpload(file);
                    }
                  }}
                />
                {uploading && <p className="upload-status">Uploading image...</p>}
                {addProductFormData.image_url && (
                  <div className="image-preview">
                    <img 
                      src={addProductFormData.image_url} 
                      alt="Main product" 
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label>Gallery Images</label>
              <div className="gallery-upload-section">
                <div className="gallery-upload-controls">
                  <input 
                    type="file" 
                    accept="image/*"
                    multiple
                    id="gallery-upload"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        handleGalleryImageUpload(files);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="gallery-upload" className="upload-btn">
                    📷 Add Images
                  </label>
                  <button 
                    type="button" 
                    className="add-more-images-btn"
                    onClick={() => document.getElementById('gallery-upload').click()}
                  >
                    ➕ Add More Images
                  </button>
                </div>
                {uploading && <p className="upload-status">Uploading gallery images...</p>}
                <div className="gallery-preview">
                  {galleryImages.map((image, index) => (
                    <div key={index} className="gallery-item">
                      <img src={image} alt={`Gallery ${index + 1}`} />
                      <button 
                        type="button" 
                        className="remove-gallery-btn"
                        onClick={() => removeGalleryImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {galleryImages.length === 0 && (
                    <div className="gallery-empty">
                      <span>📷</span>
                      <p>No gallery images added yet</p>
                      <small>Click "Add Images" to upload product photos</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Videos */}
          <div className="form-section">
            <h3>Product Videos</h3>
            <div className="form-group">
              <label>Product Video</label>
              <div className="video-upload-section">
                <input 
                  type="file" 
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleVideoUpload(file);
                    }
                  }}
                />
                {uploading && <p className="upload-status">Uploading video...</p>}
                {addProductFormData.video_url && (
                  <div className="video-preview">
                    <video 
                      controls 
                      width="100%" 
                      height="200"
                      src={addProductFormData.video_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <button 
                      type="button" 
                      className="remove-video-btn"
                      onClick={() => setAddProductFormData(prev => ({ ...prev, video_url: '' }))}
                    >
                      Remove Video
                    </button>
                  </div>
                )}
                <div className="video-info">
                  <p><strong>Supported formats:</strong> MP4, WebM, OGG, MOV</p>
                  <p><strong>Maximum size:</strong> 100MB</p>
                  <p><strong>Recommended:</strong> 720p or higher resolution</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="form-section">
            <h3>Product Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Weight</label>
                <div className="weight-input-group">
                  <input 
                    type="number" 
                    step="0.01" 
                    placeholder="0.00"
                    value={addProductFormData.weight}
                    onChange={(e) => setAddProductFormData(prev => ({ ...prev, weight: e.target.value }))}
                  />
                  <select 
                    value={addProductFormData.weight_unit}
                    onChange={(e) => setAddProductFormData(prev => ({ ...prev, weight_unit: e.target.value }))}
                  >
                    <option value="g">Grams (g)</option>
                    <option value="kg">Kilograms (kg)</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Dimensions (L×W×H)</label>
                <div className="dimensions-input-group">
                  <input 
                    type="text" 
                    placeholder="e.g., 10×5×2"
                    value={addProductFormData.dimensions}
                    onChange={(e) => setAddProductFormData(prev => ({ ...prev, dimensions: e.target.value }))}
                  />
                  <select
                    value={addProductFormData.dimension_unit || 'cm'}
                    onChange={(e) => setAddProductFormData(prev => ({ ...prev, dimension_unit: e.target.value }))}
                  >
                    <option value="cm">Centimeters (cm)</option>
                    <option value="mm">Millimeters (mm)</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Color</label>
                <input 
                  type="text" 
                  placeholder="e.g., Red, Blue, Black"
                  value={addProductFormData.color}
                  onChange={(e) => setAddProductFormData(prev => ({ ...prev, color: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Material</label>
                <input 
                  type="text" 
                  placeholder="e.g., Cotton, Plastic, Metal"
                  value={addProductFormData.material}
                  onChange={(e) => setAddProductFormData(prev => ({ ...prev, material: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="form-section">
            <h3>Specifications</h3>
            <div className="specifications-section">
              {specifications.map((spec, index) => (
                <div key={index} className="specification-row">
                  <input 
                    type="text" 
                    placeholder="Specification name"
                    value={spec.key}
                    onChange={(e) => updateSpecification(index, 'key', e.target.value)}
                  />
                  <input 
                    type="text" 
                    placeholder="Specification value"
                    value={spec.value}
                    onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-spec-btn"
                    onClick={() => removeSpecification(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-spec-btn" onClick={addSpecification}>
                + Add Specification
              </button>
            </div>
          </div>

          {/* Features */}
          <div className="form-section">
            <h3>Key Features</h3>
            <div className="features-section">
              {features.map((feature, index) => (
                <div key={index} className="feature-row">
                  <input 
                    type="text" 
                    placeholder="Enter feature"
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-feature-btn"
                    onClick={() => removeFeature(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-feature-btn" onClick={addFeature}>
                + Add Feature
              </button>
            </div>
          </div>

          {/* Tags */}
          <div className="form-section">
            <h3>Tags</h3>
            <div className="tags-section">
              {tags.map((tag, index) => (
                <div key={index} className="tag-row">
                  <input 
                    type="text" 
                    placeholder="Enter tag"
                    value={tag}
                    onChange={(e) => updateTag(index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-tag-btn"
                    onClick={() => removeTag(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-tag-btn" onClick={addTag}>
                + Add Tag
              </button>
            </div>
          </div>

          {/* Product Variations */}
          <div className="form-section">
            <h3>Product Variations</h3>
            <div className="variations-toggle">
              <input 
                type="checkbox" 
                id="has_variations"
                checked={addProductFormData.has_variations}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, has_variations: e.target.checked }))}
              />
              <label htmlFor="has_variations">This product has variations (e.g., Size, Color, Material)</label>
            </div>

            {addProductFormData.has_variations && (
              <div className="variations-section">
                {/* Variation Types */}
                <div className="variation-types">
                  <h4>Variation Types</h4>
                  {variationTypes.map((type, typeIndex) => (
                    <div key={typeIndex} className="variation-type-row">
                      <div className="type-inputs">
                        <input 
                          type="text" 
                          placeholder="Variation type (e.g., Size, Color)"
                          value={type.name}
                          onChange={(e) => updateVariationType(typeIndex, 'name', e.target.value)}
                        />
                        <div className="options-section">
                          <label>Options:</label>
                          <div className="options-list">
                            {type.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="option-input-row">
                                <input 
                                  type="text" 
                                  placeholder={`Option ${optionIndex + 1}`}
                                  value={option}
                                  onChange={(e) => updateVariationOption(typeIndex, optionIndex, e.target.value)}
                                />
                                <button 
                                  type="button" 
                                  className="remove-option-btn"
                                  onClick={() => removeVariationOption(typeIndex, optionIndex)}
                                  disabled={type.options.length === 1}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button 
                              type="button" 
                              className="add-option-btn"
                              onClick={() => addVariationOption(typeIndex)}
                            >
                              + Add Option
                            </button>
                          </div>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="remove-variation-type-btn"
                        onClick={() => removeVariationType(typeIndex)}
                      >
                        Remove Type
                      </button>
                    </div>
                  ))}
                  <button type="button" className="add-variation-type-btn" onClick={addVariationType}>
                    + Add Variation Type
                  </button>
                </div>

                {/* Generated Variations */}
                {variations.length > 0 && (
                  <div className="generated-variations">
                    <h4>Generated Variations ({variations.length})</h4>
                    <div className="variations-grid">
                      {variations.map((variation, index) => (
                        <div key={index} className="variation-card">
                          <div className="variation-header">
                            <h5>{variation.name}</h5>
                            <label className="variation-active">
                              <input 
                                type="checkbox" 
                                checked={variation.is_active}
                                onChange={(e) => updateVariation(index, 'is_active', e.target.checked)}
                              />
                              Active
                            </label>
                          </div>
                          
                          <div className="variation-content">
                            <div className="variation-image">
                              <label>Variation Image</label>
                              <input 
                                type="file" 
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleVariationImageUpload(file, index);
                                  }
                                }}
                              />
                              {variation.image_url && (
                                <div className="variation-image-preview">
                                  <img src={variation.image_url} alt={variation.name} />
                                </div>
                              )}
                            </div>
                            
                            <div className="variation-details">
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Price ($)</label>
                                  <input 
                                    type="number" 
                                    step="0.01" 
                                    placeholder="0.00"
                                    value={variation.price}
                                    onChange={(e) => updateVariation(index, 'price', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Original Price ($)</label>
                                  <input 
                                    type="number" 
                                    step="0.01" 
                                    placeholder="0.00"
                                    value={variation.original_price}
                                    onChange={(e) => updateVariation(index, 'original_price', e.target.value)}
                                  />
                                </div>
                              </div>
                              
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Stock</label>
                                  <input 
                                    type="number" 
                                    placeholder="0"
                                    value={variation.stock}
                                    onChange={(e) => updateVariation(index, 'stock', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>SKU</label>
                                  <input 
                                    type="text" 
                                    placeholder="Variation SKU"
                                    value={variation.sku}
                                    onChange={(e) => updateVariation(index, 'sku', e.target.value)}
                                  />
                                </div>
                              </div>
                              
                              <div className="form-group">
                                <label>Weight</label>
                                <div className="weight-input-group">
                                  <input 
                                    type="number" 
                                    step="0.01" 
                                    placeholder="0.00"
                                    value={variation.weight}
                                    onChange={(e) => updateVariation(index, 'weight', e.target.value)}
                                  />
                                  <select 
                                    value={variation.weight_unit || 'g'}
                                    onChange={(e) => updateVariation(index, 'weight_unit', e.target.value)}
                                  >
                                    <option value="g">Grams (g)</option>
                                    <option value="kg">Kilograms (kg)</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Policies */}
          <div className="form-section">
            <h3>Policies & Information</h3>
            <div className="form-group">
              <label>Warranty</label>
              <input 
                type="text" 
                placeholder="e.g., 1 year warranty"
                value={addProductFormData.warranty}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, warranty: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Shipping Information</label>
              <textarea 
                rows="3" 
                placeholder="Shipping details and costs"
                value={addProductFormData.shipping_info}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, shipping_info: e.target.value }))}
              ></textarea>
            </div>
            <div className="form-group">
              <label>Return Policy</label>
              <textarea 
                rows="3" 
                placeholder="Return and refund policy"
                value={addProductFormData.return_policy}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, return_policy: e.target.value }))}
              ></textarea>
            </div>
          </div>

          {/* SEO */}
          <div className="form-section">
            <h3>SEO Information</h3>
            <div className="form-group">
              <label>Meta Title</label>
              <input 
                type="text" 
                placeholder="SEO title for search engines"
                value={addProductFormData.meta_title}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, meta_title: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Meta Description</label>
              <textarea 
                rows="3" 
                placeholder="SEO description for search engines"
                value={addProductFormData.meta_description}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, meta_description: e.target.value }))}
              ></textarea>
            </div>
            <div className="form-group">
              <label>SEO Keywords</label>
              <input 
                type="text" 
                placeholder="Keywords separated by commas"
                value={addProductFormData.seo_keywords}
                onChange={(e) => setAddProductFormData(prev => ({ ...prev, seo_keywords: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => setActiveSection('products')}>
              Cancel
            </button>
            <button type="button" className="draft-btn" onClick={() => handleSaveAsDraft()}>
              Save as Draft
            </button>
            <button type="submit" className="save-btn">
              {editingAdminProduct ? 'Update Product' : 'Save Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="admin-users">
      <div className="section-header">
        <h2>Manage Customers</h2>
      </div>
      
      {loading ? (
        <div className="loading">Loading customers...</div>
      ) : (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.phone || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${user.status || 'active'}`}>
                      {user.status || 'Active'}
                    </span>
                  </td>
                  <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <button className="view-btn" onClick={() => handleViewUser(user)}>View</button>
                    <button className="delete-btn" onClick={() => handleDeleteUser(user)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderCategories = () => (
    <div className="admin-categories">
      <div className="section-header">
        <h2>Manage Categories & Subcategories</h2>
        <div className="header-actions">
        <button className="add-btn" onClick={handleAddCategory}>
          <span>➕</span> Add Category
        </button>
          <button className="add-btn secondary" onClick={handleAddSubcategory}>
            <span>➕</span> Add Subcategory
          </button>
        </div>
      </div>
      
      {/* Categories Section */}
      <div className="categories-section">
        <h3>Categories</h3>
      {loading ? (
        <div className="loading">Loading categories...</div>
      ) : (
        <div className="categories-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(category => (
                <tr key={category.id}>
                  <td>{category.id}</td>
                  <td>{category.name}</td>
                  <td>{category.description || 'No description'}</td>
                  <td>
                    <span className={`status-badge ${category.status || 'active'}`}>
                      {category.status || 'Active'}
                    </span>
                  </td>
                  <td>{new Date(category.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="edit-btn" onClick={() => handleEditCategory(category)}>Edit</button>
                    <button className="delete-btn" onClick={() => handleDeleteCategory(category)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Subcategories Section */}
      <div className="subcategories-section">
        <h3>Subcategories</h3>
        {loading ? (
          <div className="loading">Loading subcategories...</div>
        ) : (
          <div className="subcategories-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Parent Category</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subcategories.map(subcategory => (
                  <tr key={subcategory.id}>
                    <td>{subcategory.id}</td>
                    <td>{subcategory.name}</td>
                    <td>{subcategory.categories?.name || 'Unknown'}</td>
                    <td>{subcategory.description || 'No description'}</td>
                    <td>
                      <span className={`status-badge ${subcategory.status || 'active'}`}>
                        {subcategory.status || 'Active'}
                      </span>
                    </td>
                    <td>{new Date(subcategory.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className="edit-btn" onClick={() => handleEditSubcategory(subcategory)}>Edit</button>
                      <button className="delete-btn" onClick={() => handleDeleteSubcategory(subcategory)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderVendorRequests = () => (
    <div className="admin-vendor-requests">
      <div className="section-header">
        <h2>Vendor Approval Requests</h2>
        <p className="section-description">
          Review and approve new vendor registration requests
        </p>
      </div>
      
      {loading ? (
        <div className="loading">Loading vendor requests...</div>
      ) : vendorRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Pending Requests</h3>
          <p>All vendor requests have been processed.</p>
        </div>
      ) : (
        <div className="vendor-requests-table">
          <table>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Request Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendorRequests.map(vendor => (
                <tr key={vendor.id}>
                  <td>
                    <div className="vendor-info">
                      <strong>{vendor.business_name || 'N/A'}</strong>
                    </div>
                  </td>
                  <td>{vendor.email}</td>
                  <td>{vendor.phone || 'N/A'}</td>
                  <td>{new Date(vendor.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className="status-badge pending">
                      Pending Approval
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="approve-btn" 
                        onClick={() => handleApproveVendor(vendor)}
                        title="Approve vendor"
                      >
                        ✅ Approve
                      </button>
                      <button 
                        className="reject-btn" 
                        onClick={() => handleRejectVendor(vendor)}
                        title="Reject vendor"
                      >
                        ❌ Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderApprovedVendors = () => (
    <div className="admin-approved-vendors">
      <div className="section-header">
        <h2>Approved Vendors</h2>
        <p className="section-description">
          Manage all approved vendors and their accounts
        </p>
      </div>
      
      {loading ? (
        <div className="loading">Loading approved vendors...</div>
      ) : approvedVendors.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No Approved Vendors</h3>
          <p>No vendors have been approved yet.</p>
        </div>
      ) : (
        <div className="approved-vendors-table">
          <table>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Approval Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvedVendors.map(vendor => (
                <tr key={vendor.id}>
                  <td>
                    <div className="vendor-info">
                      <strong>{vendor.business_name || 'N/A'}</strong>
                    </div>
                  </td>
                  <td>{vendor.email}</td>
                  <td>{vendor.phone || 'N/A'}</td>
                  <td>{new Date(vendor.updated_at || vendor.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className="status-badge active">
                      Active
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="view-btn" 
                        onClick={() => handleViewVendor(vendor)}
                        title="View vendor details"
                      >
                        👁️ View
                      </button>
                      {vendor.status === 'active' ? (
                        <button 
                          className="suspend-btn" 
                          onClick={() => handleSuspendVendor(vendor)}
                          title="Suspend vendor"
                        >
                          ⏸️ Suspend
                        </button>
                      ) : (
                        <button 
                          className="reactivate-btn" 
                          onClick={() => handleReactivateVendor(vendor)}
                          title="Reactivate vendor"
                        >
                          ▶️ Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderQueries = () => (
    <div className="admin-queries">
      <div className="section-header">
        <h2>Contact Queries</h2>
        <div className="query-stats">
          <span className="stat-item">
            Total: {contactQueries.length}
          </span>
          <span className="stat-item">
            Pending: {contactQueries.filter(q => q.status === 'pending').length}
          </span>
          <span className="stat-item">
            In Progress: {contactQueries.filter(q => q.status === 'in_progress').length}
          </span>
          <span className="stat-item">
            Resolved: {contactQueries.filter(q => q.status === 'resolved').length}
          </span>
        </div>
      </div>
      
      {loading ? (
        <div className="loading">Loading queries...</div>
      ) : contactQueries.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📧</div>
          <h3>No Contact Queries</h3>
          <p>No contact queries have been submitted yet.</p>
        </div>
      ) : (
        <div className="queries-grid">
          {contactQueries.map((query) => (
            <div key={query.id} className="query-card">
              <div className="query-header">
                <div className="query-info">
                  <h4>{query.name || 'Unknown Name'}</h4>
                  <p className="query-email">{query.email || 'No email provided'}</p>
                  <p className="query-subject">Subject: {query.subject || 'No subject'}</p>
                </div>
                <div className="query-status">
                  <span className={`status-badge ${query.status || 'pending'}`}>
                    {(query.status || 'pending').replace('_', ' ')}
                  </span>
                </div>
              </div>
              
              <div className="query-content">
                <p className="query-message">{query.message || 'No message content'}</p>
                <div className="query-meta">
                  <span className="query-date">
                    {query.created_at ? new Date(query.created_at).toLocaleDateString() : 'Unknown date'}
                  </span>
                  {query.admin_notes && (
                    <span className="has-notes">Has admin notes</span>
                  )}
                </div>
              </div>
              
              <div className="query-actions">
                <button 
                  className="view-btn"
                  onClick={() => handleViewQuery(query)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSiteSettings = () => (
    <div className="site-settings">
      <div className="section-header">
        <h2>Site Settings</h2>
        <p>Manage website appearance and configuration</p>
      </div>
      
      <div className="settings-content">
        <div className="setting-card">
          <div className="setting-header">
            <h3>Hero Section Background Image</h3>
            <p>Upload and manage the main background image displayed on the homepage hero section</p>
          </div>
          
          <div className="setting-body">
            {heroBackgroundImage && (
              <div className="current-background-preview">
                <h4>Current Background Image:</h4>
                <div className="background-preview">
                  <img 
                    src={heroBackgroundImage} 
                    alt="Current hero background" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '400px', 
                      height: '200px', 
                      objectFit: 'cover', 
                      borderRadius: '8px',
                      border: '2px solid #e9ecef' 
                    }} 
                  />
                </div>
              </div>
            )}
            
            <div className="upload-section">
              <div className="upload-area">
                <input
                  type="file"
                  id="hero-background-upload"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleHeroBackgroundUpload(e.target.files[0]);
                    }
                  }}
                  style={{ display: 'none' }}
                />
                <label htmlFor="hero-background-upload" className="upload-label">
                  {settingsLoading ? (
                    <div className="uploading-state">
                      <span>⏳</span>
                      <span>Uploading...</span>
                    </div>
                  ) : (
                    <div className="upload-content">
                      <span>📸</span>
                      <h4>Upload New Background Image</h4>
                      <p>Click to select an image file (JPG, PNG, WebP)</p>
                      <div className="upload-requirements">
                        <small>Recommended: 1920x1080 or higher resolution</small>
                        <small>Max file size: 10MB</small>
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            <div className="setting-info">
              <div className="info-item">
                <strong>Current Image URL:</strong>
                <code>{heroBackgroundImage || 'No image set'}</code>
              </div>
              <div className="info-item">
                <strong>Usage:</strong>
                <span>This image appears as the background of the "Welcome to Inifiy" section on the homepage</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Future settings can be added here */}
        <div className="setting-card coming-soon">
          <div className="setting-header">
            <h3>Additional Settings</h3>
            <p>More site configuration options coming soon...</p>
          </div>
          <div className="setting-body">
            <div className="placeholder-content">
              <span>🚧</span>
              <p>Future settings like site title, logo, colors, and more will be available here.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderShipping = () => (
    <div className="admin-shipping">
      <div className="section-header">
        <h2>Shipping Rates</h2>
        <button className="add-btn" onClick={handleAddShipping}>
          <span>➕</span> Add Shipping Rate
        </button>
      </div>
      
      {loading ? (
        <div className="loading">Loading shipping rates...</div>
      ) : shippingRates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🚚</div>
          <h3>No Shipping Rates</h3>
          <p>No shipping rates have been configured yet.</p>
        </div>
      ) : (
        <div className="shipping-table">
          <table>
            <thead>
              <tr>
                <th>Country</th>
                <th>Country Code</th>
                <th>Shipping Cost</th>
                <th>Delivery Days</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shippingRates.map(shipping => (
                <tr key={shipping.id}>
                  <td>
                    <div className="country-info">
                      <strong>{shipping.country_name}</strong>
                    </div>
                  </td>
                  <td>{shipping.country_code || 'N/A'}</td>
                  <td>{getCurrencySymbolForCountry(shipping.country_name)}{parseFloat(shipping.shipping_cost).toFixed(2)}</td>
                  <td>{shipping.estimated_delivery_days} days</td>
                  <td>
                    <span className={`status-badge ${shipping.is_active ? 'active' : 'inactive'}`}>
                      {shipping.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-btn" 
                        onClick={() => handleEditShipping(shipping)}
                        title="Edit shipping rate"
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="delete-btn" 
                        onClick={() => handleDeleteShipping(shipping)}
                        title="Delete shipping rate"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderFeaturedProducts = () => {
    // Filter products by selected currency
    const filteredProducts = products.filter(product => {
      if (selectedCurrency === 'ALL') {
        return true; // Show all products regardless of currency
      }
      
      let hasCurrencyPricing = false;
      switch (selectedCurrency) {
        case 'USD':
          hasCurrencyPricing = product.price_usd && parseFloat(product.price_usd) > 0;
          break;
        case 'GBP':
          hasCurrencyPricing = product.price_gbp && parseFloat(product.price_gbp) > 0;
          break;
        case 'AED':
          hasCurrencyPricing = product.price_aed && parseFloat(product.price_aed) > 0;
          break;
        case 'SAR':
          hasCurrencyPricing = product.price_sar && parseFloat(product.price_sar) > 0;
          break;
        default:
          hasCurrencyPricing = true;
      }
      return hasCurrencyPricing;
    });

    // Debug logging for currency filtering
    console.log(`Featured Products - Selected Currency: ${selectedCurrency}`);
    console.log(`Featured Products - Total products: ${products.length}`);
    console.log(`Featured Products - Products with ${selectedCurrency} pricing: ${filteredProducts.length}`);
    console.log(`Featured Products - Sample filtered products:`, filteredProducts.slice(0, 3).map(p => ({
      name: p.name,
      price: p[`price_${selectedCurrency.toLowerCase()}`],
      hasPricing: p[`price_${selectedCurrency.toLowerCase()}`] && parseFloat(p[`price_${selectedCurrency.toLowerCase()}`]) > 0
    })));

    return (
      <div className="admin-featured-products">
        <div className="section-header">
          <h2>Featured Products</h2>
          <p>Products with {selectedCurrency} pricing</p>
        </div>
        
        {/* Currency Filter */}
        <div className="filters-section">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="featured-currency-select">Select Currency:</label>
              <select 
                id="featured-currency-select"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="filter-select"
              >
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="AED">AED (د.إ)</option>
                <option value="SAR">SAR (ر.س)</option>
                <option value="ALL">All Currencies</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Currency Filter Summary */}
        <div className="currency-filter-summary">
          <span className="filter-info">
            Showing products with {selectedCurrency} pricing only
          </span>
          <span className="product-count">
            {filteredProducts.length} products available
          </span>
        </div>
        
        {loading ? (
          <div className="loading">Loading featured products...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Products Found</h3>
            <p>No products found with {selectedCurrency} pricing.</p>
          </div>
        ) : (
        <div className="featured-products-table">
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Product Name</th>
                <th>Vendor</th>
                <th>Category</th>
                <th className="selected-currency">Price ({selectedCurrency})</th>
                <th>Stock</th>
                <th>Countries</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => (
                <tr key={product.id}>
                  <td>
                    <div className="product-image-cell">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name} 
                          className="product-thumbnail"
                        />
                      ) : (
                        <div className="product-placeholder">📦</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="product-name-cell">
                      <strong>{product.name || 'Unnamed Product'}</strong>
                      {product.description && (
                        <p className="product-description">
                          {product.description.length > 50 
                            ? `${product.description.substring(0, 50)}...` 
                            : product.description
                          }
                        </p>
                      )}
                    </div>
                  </td>
                  <td>{product.vendor || 'Unknown Vendor'}</td>
                  <td>
                    <span className="category-badge">
                      {product.category || 'Uncategorized'}
                    </span>
                  </td>
                  <td>
                    <span className={`price-${selectedCurrency.toLowerCase()}`}>
                      {(() => {
                        const price = product[`price_${selectedCurrency.toLowerCase()}`];
                        if (price && parseFloat(price) > 0) {
                          const symbol = selectedCurrency === 'USD' ? '$' : 
                                       selectedCurrency === 'GBP' ? '£' : 
                                       selectedCurrency === 'AED' ? 'د.إ' : 
                                       selectedCurrency === 'SAR' ? 'ر.س' : '$';
                          return `${symbol}${parseFloat(price).toFixed(2)}`;
                        }
                        return 'N/A';
                      })()}
                    </span>
                  </td>
                  <td>
                    <span className={`stock-badge ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                      {product.stock || 0}
                    </span>
                  </td>
                  <td>
                    <div className="countries-cell">
                      {product.countries && product.countries.length > 0 ? (
                        product.countries.map((country, index) => (
                          <span key={index} className="country-badge">
                            {country}
                          </span>
                        ))
                      ) : (
                        <span className="no-countries">No countries set</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${product.status || 'active'}`}>
                      {product.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    <span className="created-date">
                      {new Date(product.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className={`pin-btn ${product.featured ? 'pinned' : ''}`}
                        onClick={() => handleToggleFeatured(product)}
                        title={product.featured ? 'Unpin from featured' : 'Pin to featured'}
                      >
                        {product.featured ? '📌 Pinned' : '📌 Pin'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    );
  };

  const renderFeaturedCategory = () => {
    // Filter products based on selected currency and category (using flexible matching like main website)
    const filteredProducts = products.filter(product => {
      // Filter by currency
      let currencyMatch = false;
      switch (selectedCurrency) {
        case 'USD':
          currencyMatch = product.price_usd && parseFloat(product.price_usd) > 0;
          break;
        case 'GBP':
          currencyMatch = product.price_gbp && parseFloat(product.price_gbp) > 0;
          break;
        case 'AED':
          currencyMatch = product.price_aed && parseFloat(product.price_aed) > 0;
          break;
        case 'SAR':
          currencyMatch = product.price_sar && parseFloat(product.price_sar) > 0;
          break;
        default:
          currencyMatch = true;
      }

      // Filter by category using flexible matching (same logic as main website)
      let categoryMatch = false;
      if (product.category) {
        const productCategory = product.category.toLowerCase().trim();
        const selectedCat = selectedCategoryFilter.toLowerCase().trim();
        
        // Exact match
        categoryMatch = productCategory === selectedCat;
        
        // If no exact match, try partial matching for similar categories
        if (!categoryMatch) {
          // Handle variations like "Pet Care" vs "Pet Supplies"
          if (selectedCat.includes('pet') || productCategory.includes('pet')) {
            categoryMatch = selectedCat.includes('pet') && productCategory.includes('pet');
          }
          // Handle variations like "Home & Kitchen" vs "Home and Kitchen"
          else if (selectedCat.includes('home') || productCategory.includes('home')) {
            categoryMatch = selectedCat.includes('home') && productCategory.includes('home');
          }
          // Handle variations like "Personal Care" vs "Personal"
          else if (selectedCat.includes('personal') || productCategory.includes('personal')) {
            categoryMatch = selectedCat.includes('personal') && productCategory.includes('personal');
          }
          // Handle variations like "Electronics" vs "Electronic"
          else if (selectedCat.includes('electronic') || productCategory.includes('electronic')) {
            categoryMatch = selectedCat.includes('electronic') && productCategory.includes('electronic');
          }
        }
      }

      // Debug logging
      if (product.category && product.category.toLowerCase().includes('pet')) {
        console.log(`Admin Featured Category - Product: "${product.name}" | Category: "${product.category}" | Currency: ${selectedCurrency} | Price: ${product[`price_${selectedCurrency.toLowerCase()}`]} | Currency Match: ${currencyMatch} | Category Match: ${categoryMatch}`);
      }

      return currencyMatch && categoryMatch;
    });

    // Debug summary
    console.log(`Admin Featured Category - Filter Results: ${filteredProducts.length} products found for ${selectedCurrency} ${selectedCategoryFilter}`);
    console.log(`Admin Featured Category - Total products in database: ${products.length}`);
    console.log(`Admin Featured Category - Products with ${selectedCurrency} pricing: ${products.filter(p => {
      switch (selectedCurrency) {
        case 'USD': return p.price_usd && parseFloat(p.price_usd) > 0;
        case 'GBP': return p.price_gbp && parseFloat(p.price_gbp) > 0;
        case 'AED': return p.price_aed && parseFloat(p.price_aed) > 0;
        case 'SAR': return p.price_sar && parseFloat(p.price_sar) > 0;
        default: return true;
      }
    }).length}`);
    console.log(`Admin Featured Category - Products in ${selectedCategoryFilter} category: ${products.filter(p => {
      if (!p.category) return false;
      const productCategory = p.category.toLowerCase().trim();
      const selectedCat = selectedCategoryFilter.toLowerCase().trim();
      return productCategory === selectedCat || 
             (selectedCat.includes('pet') && productCategory.includes('pet')) ||
             (selectedCat.includes('home') && productCategory.includes('home')) ||
             (selectedCat.includes('personal') && productCategory.includes('personal')) ||
             (selectedCat.includes('electronic') && productCategory.includes('electronic'));
    }).length}`);

    const getCurrencySymbol = (currency) => {
      switch (currency) {
        case 'USD': return '$';
        case 'GBP': return '£';
        case 'AED': return 'د.إ';
        case 'SAR': return 'ر.س';
        default: return '$';
      }
    };

    const getProductPrice = (product) => {
      switch (selectedCurrency) {
        case 'USD':
          return product.price_usd ? parseFloat(product.price_usd).toFixed(2) : 'N/A';
        case 'GBP':
          return product.price_gbp ? parseFloat(product.price_gbp).toFixed(2) : 'N/A';
        case 'AED':
          return product.price_aed ? parseFloat(product.price_aed).toFixed(2) : 'N/A';
        case 'SAR':
          return product.price_sar ? parseFloat(product.price_sar).toFixed(2) : 'N/A';
        default:
          return product.price_usd ? parseFloat(product.price_usd).toFixed(2) : 'N/A';
      }
    };

    // Helper function to check if featured category is full for current currency and category
    const isFeaturedCategoryFull = () => {
      const currentFeaturedCount = products.filter(p => {
        if (!p.is_featured_category) return false;
        
        // Check if product has price in selected currency
        let hasPriceInCurrency = false;
        switch (selectedCurrency) {
          case 'USD':
            hasPriceInCurrency = p.price_usd && parseFloat(p.price_usd) > 0;
            break;
          case 'GBP':
            hasPriceInCurrency = p.price_gbp && parseFloat(p.price_gbp) > 0;
            break;
          case 'AED':
            hasPriceInCurrency = p.price_aed && parseFloat(p.price_aed) > 0;
            break;
          case 'SAR':
            hasPriceInCurrency = p.price_sar && parseFloat(p.price_sar) > 0;
            break;
          default:
            hasPriceInCurrency = true;
        }
        
        // Check if product is in selected category using flexible matching
        let isInSelectedCategory = false;
        if (p.category) {
          const productCategory = p.category.toLowerCase().trim();
          const selectedCat = selectedCategoryFilter.toLowerCase().trim();
          
          // Exact match
          isInSelectedCategory = productCategory === selectedCat;
          
          // If no exact match, try partial matching for similar categories
          if (!isInSelectedCategory) {
            // Handle variations like "Pet Care" vs "Pet Supplies"
            if (selectedCat.includes('pet') || productCategory.includes('pet')) {
              isInSelectedCategory = selectedCat.includes('pet') && productCategory.includes('pet');
            }
            // Handle variations like "Home & Kitchen" vs "Home and Kitchen"
            else if (selectedCat.includes('home') || productCategory.includes('home')) {
              isInSelectedCategory = selectedCat.includes('home') && productCategory.includes('home');
            }
            // Handle variations like "Personal Care" vs "Personal"
            else if (selectedCat.includes('personal') || productCategory.includes('personal')) {
              isInSelectedCategory = selectedCat.includes('personal') && productCategory.includes('personal');
            }
            // Handle variations like "Electronics" vs "Electronic"
            else if (selectedCat.includes('electronic') || productCategory.includes('electronic')) {
              isInSelectedCategory = selectedCat.includes('electronic') && productCategory.includes('electronic');
            }
          }
        }
        
        return hasPriceInCurrency && isInSelectedCategory;
      }).length;
      
      return currentFeaturedCount >= 4;
    };

    return (
      <div className="admin-featured-category">
        <div className="section-header">
          <h2>Featured Category</h2>
          <p>View products by currency and category selection</p>
          <div className="featured-count-info">
            <span className="count-badge">
              {products.filter(p => {
                if (!p.is_featured_category) return false;
                
                // Check if product has price in selected currency
                let hasPriceInCurrency = false;
                switch (selectedCurrency) {
                  case 'USD':
                    hasPriceInCurrency = p.price_usd && parseFloat(p.price_usd) > 0;
                    break;
                  case 'GBP':
                    hasPriceInCurrency = p.price_gbp && parseFloat(p.price_gbp) > 0;
                    break;
                  case 'AED':
                    hasPriceInCurrency = p.price_aed && parseFloat(p.price_aed) > 0;
                    break;
                  case 'SAR':
                    hasPriceInCurrency = p.price_sar && parseFloat(p.price_sar) > 0;
                    break;
                  default:
                    hasPriceInCurrency = true;
                }
                
                // Check if product is in selected category using flexible matching
                let isInSelectedCategory = false;
                if (p.category) {
                  const productCategory = p.category.toLowerCase().trim();
                  const selectedCat = selectedCategoryFilter.toLowerCase().trim();
                  
                  // Exact match
                  isInSelectedCategory = productCategory === selectedCat;
                  
                  // If no exact match, try partial matching for similar categories
                  if (!isInSelectedCategory) {
                    // Handle variations like "Pet Care" vs "Pet Supplies"
                    if (selectedCat.includes('pet') || productCategory.includes('pet')) {
                      isInSelectedCategory = selectedCat.includes('pet') && productCategory.includes('pet');
                    }
                    // Handle variations like "Home & Kitchen" vs "Home and Kitchen"
                    else if (selectedCat.includes('home') || productCategory.includes('home')) {
                      isInSelectedCategory = selectedCat.includes('home') && productCategory.includes('home');
                    }
                    // Handle variations like "Personal Care" vs "Personal"
                    else if (selectedCat.includes('personal') || productCategory.includes('personal')) {
                      isInSelectedCategory = selectedCat.includes('personal') && productCategory.includes('personal');
                    }
                    // Handle variations like "Electronics" vs "Electronic"
                    else if (selectedCat.includes('electronic') || productCategory.includes('electronic')) {
                      isInSelectedCategory = selectedCat.includes('electronic') && productCategory.includes('electronic');
                    }
                  }
                }
                
                return hasPriceInCurrency && isInSelectedCategory;
              }).length}/4 Featured Products for {selectedCurrency} {selectedCategoryFilter}
            </span>
            {isFeaturedCategoryFull() && (
              <span className="full-notice">Section is full for {selectedCurrency} {selectedCategoryFilter}</span>
            )}
          </div>
        </div>
        
        <div className="filters-section">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="currency-select">Select Currency:</label>
              <select 
                id="currency-select"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="filter-select"
              >
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="AED">AED (د.إ)</option>
                <option value="SAR">SAR (ر.س)</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="category-select">Select Category:</label>
              <select 
                id="category-select"
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                className="filter-select"
              >
                <option value="Electronics">Electronics</option>
                <option value="Pet Care">Pet Care</option>
                <option value="Home & Kitchen">Home & Kitchen</option>
                <option value="Personal Care">Personal Care</option>
              </select>
            </div>
          </div>
        </div>

        {loading && products.length === 0 ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Products Found</h3>
            <p>No products found for the selected currency ({selectedCurrency}) and category ({selectedCategoryFilter}).</p>
          </div>
        ) : (
          <div className="featured-category-table">
            <div className="table-header">
              <h3>Products with {selectedCurrency} Pricing - {selectedCategoryFilter} ({filteredProducts.length} products)</h3>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Product Name</th>
                  <th>Vendor</th>
                  <th>Category</th>
                  <th>Price ({selectedCurrency})</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id}>
                    <td>
                      <div className="product-image-cell">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name} 
                            className="product-thumbnail"
                          />
                        ) : (
                          <div className="product-placeholder">📦</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="product-name-cell">
                        <strong>{product.name || 'Unnamed Product'}</strong>
                        {product.description && (
                          <p className="product-description">
                            {product.description.length > 50 
                              ? `${product.description.substring(0, 50)}...` 
                              : product.description
                            }
                          </p>
                        )}
                      </div>
                    </td>
                    <td>{product.vendor || 'Unknown Vendor'}</td>
                    <td>
                      <span className="category-badge">
                        {product.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td>
                      <span className={`price-${selectedCurrency.toLowerCase()}`}>
                        {getProductPrice(product) !== 'N/A' 
                          ? `${getCurrencySymbol(selectedCurrency)}${getProductPrice(product)}` 
                          : 'N/A'
                        }
                      </span>
                    </td>
                    <td>
                      <span className={`stock-badge ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                        {product.stock || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${product.status}`}>
                        {product.status || 'active'}
                      </span>
                    </td>
                    <td>
                      {product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className={`action-btn pin-btn ${product.is_featured_category ? 'pinned' : ''} ${!product.is_featured_category && isFeaturedCategoryFull() ? 'disabled' : ''}`}
                          onClick={() => handleTogglePin(product)}
                          title={product.is_featured_category ? 'Remove from Featured Category' : isFeaturedCategoryFull() ? `Featured Category section is full (4/4) for ${selectedCurrency} ${selectedCategoryFilter}` : 'Add to Featured Category'}
                          disabled={!product.is_featured_category && isFeaturedCategoryFull()}
                        >
                          📌
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderHotDeals = () => {
    const getCurrencySymbol = (currency) => {
      if (currency === '$') return '$';
      else if (currency === '£') return '£';
      else if (currency === 'AED') return 'د.إ';
      else if (currency === 'KSA') return 'ر.س';
      return '£'; // Default fallback
    };

    const getProductPrice = (product) => {
      if (selectedCurrency === '$') {
        return product.price_usd || 0;
      } else if (selectedCurrency === '£') {
        return product.price_gbp || 0;
      } else if (selectedCurrency === 'AED') {
        return product.price_aed || 0;
      } else if (selectedCurrency === 'KSA') {
        return product.price_sar || 0;
      }
      // Default fallback to GBP
      return product.price_gbp || 0;
    };

    // Helper function to check if a product is selected in any hot deals section
    const isProductSelected = (product) => {
      return product.is_hot_product || product.is_todays_deal || product.is_new_arrival || product.is_exclusive_offer;
    };

    // Helper function to get which category a product belongs to
          const getProductCategory = (product) => {
        if (product.is_hot_product) return 'hot_products';
        if (product.is_todays_deal) return 'todays_deals';
        if (product.is_new_arrival) return 'new_arrivals';
        if (product.is_exclusive_offer) return 'exclusive_offers';
        return null;
      };

          // Helper function to check if a product is already in any hot deals section
    const isProductInAnySection = (product) => {
      return hotDealsProducts.hot_products.find(p => p.id === product.id) ||
             hotDealsProducts.todays_deals.find(p => p.id === product.id) ||
             hotDealsProducts.new_arrivals.find(p => p.id === product.id) ||
             hotDealsProducts.exclusive_offers.find(p => p.id === product.id);
    };

    // Helper function to get currency-specific count for a section
    const getCurrencyCountForSection = (section) => {
      return hotDealsProducts[section].filter(p => {
        let hasCurrencyPricing = false;
        switch (selectedCurrency) {
          case 'USD':
            hasCurrencyPricing = p.price_usd && parseFloat(p.price_usd) > 0;
            break;
          case 'GBP':
            hasCurrencyPricing = p.price_gbp && parseFloat(p.price_gbp) > 0;
            break;
          case 'AED':
            hasCurrencyPricing = p.price_aed && parseFloat(p.price_aed) > 0;
            break;
          case 'SAR':
            hasCurrencyPricing = p.price_sar && parseFloat(p.price_sar) > 0;
            break;
          default:
            hasCurrencyPricing = true;
        }
        return hasCurrencyPricing;
      }).length;
    };

    // Helper function to get currency-specific products for a section
    const getCurrencyProductsForSection = (section) => {
      return hotDealsProducts[section].filter(p => {
        let hasCurrencyPricing = false;
        switch (selectedCurrency) {
          case 'USD':
            hasCurrencyPricing = p.price_usd && parseFloat(p.price_usd) > 0;
            break;
          case 'GBP':
            hasCurrencyPricing = p.price_gbp && parseFloat(p.price_gbp) > 0;
            break;
          case 'AED':
            hasCurrencyPricing = p.price_aed && parseFloat(p.price_aed) > 0;
            break;
          case 'SAR':
            hasCurrencyPricing = p.price_sar && parseFloat(p.price_sar) > 0;
            break;
          default:
            hasCurrencyPricing = true;
        }
        return hasCurrencyPricing;
      });
    };

    return (
      <div className="admin-hot-deals">
        <div className="section-header">
          <h2>Hot Deals Management</h2>
          <p>Manage products in Hot Products, Today's Deals, New Arrivals, and Exclusive Offers sections</p>
        </div>
        
        <div className="filters-section">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="currency-select">Select Currency:</label>
              <select 
                id="currency-select"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="filter-select"
              >
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="AED">AED (د.إ)</option>
                <option value="SAR">SAR (ر.س)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Currency Summary */}
        <div className="currency-summary">
          <h3>📊 Currency Summary for {selectedCurrency}</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">🔥 Hot Products:</span>
              <span className="summary-count">{getCurrencyCountForSection('hot_products')}/4</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">🎯 Today's Deals:</span>
              <span className="summary-count">{getCurrencyCountForSection('todays_deals')}/4</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">🆕 New Arrivals:</span>
              <span className="summary-count">{getCurrencyCountForSection('new_arrivals')}/4</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">💎 Exclusive Offers:</span>
              <span className="summary-count">{getCurrencyCountForSection('exclusive_offers')}/4</span>
            </div>
          </div>
        </div>

        <div className="hot-deals-sections">
          {/* Hot Products Section */}
          <div className="hot-deals-section">
            <h3>🔥 Hot Products ({getCurrencyCountForSection('hot_products')}/4)</h3>
            <div className="section-content">
              {(() => {
                const currencyProducts = getCurrencyProductsForSection('hot_products');
                
                if (currencyProducts.length === 0) {
                  return (
                    <div className="empty-state">
                      <p>No hot products with {selectedCurrency} pricing</p>
                    </div>
                  );
                }
                
                return (
                  <div className="products-grid">
                    {currencyProducts.map(product => (
                      <div key={product.id} className="product-card selected-product">
                        <div className="product-image">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} />
                          ) : (
                            <div className="product-placeholder">📦</div>
                          )}
                        </div>
                        <div className="product-info">
                          <h4>{product.name}</h4>
                        </div>
                        <div className="product-actions">
                          <button 
                            className="action-btn remove-btn"
                            onClick={() => handleToggleHotDeal(product, 'hot_products')}
                            title="Remove from Hot Products"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Today's Deals Section */}
          <div className="hot-deals-section">
            <h3>🎯 Today's Deals ({getCurrencyCountForSection('todays_deals')}/4)</h3>
            <div className="section-content">
              {(() => {
                const currencyProducts = getCurrencyProductsForSection('todays_deals');
                
                if (currencyProducts.length === 0) {
                  return (
                    <div className="empty-state">
                      <p>No today's deals with {selectedCurrency} pricing</p>
                    </div>
                  );
                }
                
                return (
                  <div className="products-grid">
                    {currencyProducts.map(product => (
                      <div key={product.id} className="product-card selected-product">
                        <div className="product-image">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} />
                          ) : (
                            <div className="product-placeholder">📦</div>
                          )}
                        </div>
                        <div className="product-info">
                          <h4>{product.name}</h4>
                        </div>
                        <div className="product-actions">
                          <button 
                            className="action-btn remove-btn"
                            onClick={() => handleToggleHotDeal(product, 'todays_deals')}
                            title="Remove from Today's Deals"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* New Arrivals Section */}
          <div className="hot-deals-section">
            <h3>🆕 New Arrivals ({getCurrencyCountForSection('new_arrivals')}/4)</h3>
            <div className="section-content">
              {(() => {
                const currencyProducts = getCurrencyProductsForSection('new_arrivals');
                
                if (currencyProducts.length === 0) {
                  return (
                    <div className="empty-state">
                      <p>No new arrivals with {selectedCurrency} pricing</p>
                    </div>
                  );
                }
                
                return (
                  <div className="products-grid">
                    {currencyProducts.map(product => (
                      <div key={product.id} className="product-card selected-product">
                        <div className="product-image">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} />
                          ) : (
                            <div className="product-placeholder">📦</div>
                          )}
                        </div>
                        <div className="product-info">
                          <h4>{product.name}</h4>
                        </div>
                        <div className="product-actions">
                          <button 
                            className="action-btn remove-btn"
                            onClick={() => handleToggleHotDeal(product, 'new_arrivals')}
                            title="Remove from New Arrivals"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Exclusive Offers Section */}
          <div className="hot-deals-section">
            <h3>💎 Exclusive Offers ({getCurrencyCountForSection('exclusive_offers')}/4)</h3>
            <div className="section-content">
              {(() => {
                const currencyProducts = getCurrencyProductsForSection('exclusive_offers');
                
                if (currencyProducts.length === 0) {
                  return (
                    <div className="empty-state">
                      <p>No exclusive offers with {selectedCurrency} pricing</p>
                    </div>
                  );
                }
                
                return (
                  <div className="products-grid">
                    {currencyProducts.map(product => (
                      <div key={product.id} className="product-card selected-product">
                        <div className="product-image">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} />
                          ) : (
                            <div className="product-placeholder">📦</div>
                          )}
                        </div>
                        <div className="product-info">
                          <h4>{product.name}</h4>
                        </div>
                        <div className="product-actions">
                          <button 
                            className="action-btn remove-btn"
                            onClick={() => handleToggleHotDeal(product, 'exclusive_offers')}
                            title="Remove from Exclusive Offers"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Add Products to Sections */}
        <div className="add-products-section">
          <h3>Add Products to Sections</h3>
          <p>Select products to add to any of the hot deals sections. Each product can only be in one section at a time.</p>
          
          {/* Currency Filter Summary */}
          <div className="currency-filter-summary">
            <span className="filter-info">
              Showing products with {selectedCurrency} pricing only
            </span>
            <span className="product-count">
              {(() => {
                const filteredProducts = products.filter(product => {
                  const isActive = product.status === 'active';
                  const notInHotDeals = !hotDealsProducts.hot_products.find(p => p.id === product.id) &&
                                       !hotDealsProducts.todays_deals.find(p => p.id === product.id) &&
                                       !hotDealsProducts.new_arrivals.find(p => p.id === product.id) &&
                                       !hotDealsProducts.exclusive_offers.find(p => p.id === product.id);
                  
                  let hasCurrencyPricing = false;
                  switch (selectedCurrency) {
                    case 'USD':
                      hasCurrencyPricing = product.price_usd && parseFloat(product.price_usd) > 0;
                      break;
                    case 'GBP':
                      hasCurrencyPricing = product.price_gbp && parseFloat(product.price_gbp) > 0;
                      break;
                    case 'AED':
                      hasCurrencyPricing = product.price_aed && parseFloat(product.price_aed) > 0;
                      break;
                    case 'SAR':
                      hasCurrencyPricing = product.price_sar && parseFloat(product.price_sar) > 0;
                      break;
                    default:
                      hasCurrencyPricing = true;
                  }
                  
                  return isActive && notInHotDeals && hasCurrencyPricing;
                });
                
                // Debug logging
                console.log(`Hot Deals Admin - Currency: ${selectedCurrency}`);
                console.log(`Hot Deals Admin - Total products: ${products.length}`);
                console.log(`Hot Deals Admin - Active products: ${products.filter(p => p.status === 'active').length}`);
                console.log(`Hot Deals Admin - Products with ${selectedCurrency} pricing: ${products.filter(p => {
                  switch (selectedCurrency) {
                    case 'USD': return p.price_usd && parseFloat(p.price_usd) > 0;
                    case 'GBP': return p.price_gbp && parseFloat(p.price_gbp) > 0;
                    case 'AED': return p.price_aed && parseFloat(p.price_aed) > 0;
                    case 'SAR': return p.price_sar && parseFloat(p.price_sar) > 0;
                    default: return true;
                  }
                }).length}`);
                console.log(`Hot Deals Admin - Available for hot deals: ${filteredProducts.length}`);
                
                return filteredProducts.length;
              })()} products available
            </span>
          </div>
          
          <div className="products-table">
            <table>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Product Name</th>
                  <th>Vendor</th>
                  <th>Category</th>
                  <th>Price ({selectedCurrency})</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.filter(product => {
                  // First check if product is active and not already in any hot deals section
                  const isActive = product.status === 'active';
                  const notInHotDeals = !hotDealsProducts.hot_products.find(p => p.id === product.id) &&
                                       !hotDealsProducts.todays_deals.find(p => p.id === product.id) &&
                                       !hotDealsProducts.new_arrivals.find(p => p.id === product.id) &&
                                       !hotDealsProducts.exclusive_offers.find(p => p.id === product.id);
                  
                  // Then check if product has pricing in the selected currency
                  let hasCurrencyPricing = false;
                  switch (selectedCurrency) {
                    case 'USD':
                      hasCurrencyPricing = product.price_usd && parseFloat(product.price_usd) > 0;
                      break;
                    case 'GBP':
                      hasCurrencyPricing = product.price_gbp && parseFloat(product.price_gbp) > 0;
                      break;
                    case 'AED':
                      hasCurrencyPricing = product.price_aed && parseFloat(product.price_aed) > 0;
                      break;
                    case 'SAR':
                      hasCurrencyPricing = product.price_sar && parseFloat(product.price_sar) > 0;
                      break;
                    default:
                      hasCurrencyPricing = true;
                  }
                  
                  return isActive && notInHotDeals && hasCurrencyPricing;
                }).map(product => (
                  <tr key={product.id}>
                    <td>
                      <div className="product-image-cell">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name} 
                            className="product-thumbnail"
                          />
                        ) : (
                          <div className="product-placeholder">📦</div>
                        )}
                      </div>
                    </td>
                    <td>{product.name}</td>
                    <td>{product.vendor_name || 'N/A'}</td>
                    <td>{product.category || 'N/A'}</td>
                    <td>
                      <span className={`price-${selectedCurrency.toLowerCase()}`}>
                        {getProductPrice(product) !== 'N/A' 
                          ? `${getCurrencySymbol(selectedCurrency)}${getProductPrice(product)}` 
                          : 'N/A'
                        }
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${product.status}`}>
                        {product.status || 'active'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className={`action-btn add-btn ${getCurrencyCountForSection('hot_products') >= 4 || isProductInAnySection(product) ? 'disabled' : ''} ${hotDealsProducts.hot_products.find(p => p.id === product.id) ? 'in-section' : ''}`}
                          onClick={() => handleToggleHotDeal(product, 'hot_products')}
                          title={getCurrencyCountForSection('hot_products') >= 4 ? `Hot Products section is full (4/4) for ${selectedCurrency}` : isProductInAnySection(product) ? "Product is already in another section" : "Add to Hot Products"}
                          disabled={getCurrencyCountForSection('hot_products') >= 4 || isProductInAnySection(product)}
                        >
                          {isProductInAnySection(product) ? (hotDealsProducts.hot_products.find(p => p.id === product.id) ? "In Hot" : "Already Added") : `Hot (${getCurrencyCountForSection('hot_products')}/4)`}
                        </button>
                        <button 
                          className={`action-btn add-btn ${getCurrencyCountForSection('todays_deals') >= 4 || isProductInAnySection(product) ? 'disabled' : ''} ${hotDealsProducts.todays_deals.find(p => p.id === product.id) ? 'in-section' : ''}`}
                          onClick={() => handleToggleHotDeal(product, 'todays_deals')}
                          title={getCurrencyCountForSection('todays_deals') >= 4 ? `Today's Deals section is full (4/4) for ${selectedCurrency}` : isProductInAnySection(product) ? "Product is already in another section" : "Add to Today's Deals"}
                          disabled={getCurrencyCountForSection('todays_deals') >= 4 || isProductInAnySection(product)}
                        >
                          {isProductInAnySection(product) ? (hotDealsProducts.todays_deals.find(p => p.id === product.id) ? "In Deals" : "Already Added") : `Deals (${getCurrencyCountForSection('todays_deals')}/4)`}
                        </button>
                        <button 
                          className={`action-btn add-btn ${getCurrencyCountForSection('new_arrivals') >= 4 || isProductInAnySection(product) ? 'disabled' : ''} ${hotDealsProducts.new_arrivals.find(p => p.id === product.id) ? 'in-section' : ''}`}
                          onClick={() => handleToggleHotDeal(product, 'new_arrivals')}
                          title={getCurrencyCountForSection('new_arrivals') >= 4 ? `New Arrivals section is full (4/4) for ${selectedCurrency}` : isProductInAnySection(product) ? "Product is already in another section" : "Add to New Arrivals"}
                          disabled={getCurrencyCountForSection('new_arrivals') >= 4 || isProductInAnySection(product)}
                        >
                          {isProductInAnySection(product) ? (hotDealsProducts.new_arrivals.find(p => p.id === product.id) ? "In New" : "Already Added") : `New (${getCurrencyCountForSection('new_arrivals')}/4)`}
                        </button>
                        <button 
                          className={`action-btn add-btn ${getCurrencyCountForSection('exclusive_offers') >= 4 || isProductInAnySection(product) ? 'disabled' : ''} ${hotDealsProducts.exclusive_offers.find(p => p.id === product.id) ? 'in-section' : ''}`}
                          onClick={() => handleToggleHotDeal(product, 'exclusive_offers')}
                          title={getCurrencyCountForSection('exclusive_offers') >= 4 ? `Exclusive Offers section is full (4/4) for ${selectedCurrency}` : isProductInAnySection(product) ? "Product is already in another section" : "Add to Exclusive Offers"}
                          disabled={getCurrencyCountForSection('exclusive_offers') >= 4 || isProductInAnySection(product)}
                        >
                          {isProductInAnySection(product) ? (hotDealsProducts.exclusive_offers.find(p => p.id === product.id) ? "In Exclusive" : "Already Added") : `Exclusive (${getCurrencyCountForSection('exclusive_offers')}/4)`}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderModal = () => {
    if (!selectedOrder) return null;

    // Helper function to get currency symbol from order
    const getOrderCurrency = () => {
      // Get customer country from the order
      const country = selectedOrder.customerCountry || '';
      const countryLower = country.toLowerCase();
      
      if (countryLower.includes('united kingdom') || countryLower.includes('uk') || countryLower.includes('britain') || countryLower.includes('england')) {
        return '£';
      } else if (countryLower.includes('united states') || countryLower.includes('usa') || countryLower.includes('america')) {
        return '$';
      } else if (countryLower.includes('uae') || countryLower.includes('united arab emirates') || countryLower.includes('emirates')) {
        return 'د.إ';
      } else if (countryLower.includes('saudi') || countryLower.includes('ksa') || countryLower.includes('arabia')) {
        return 'ر.س';
      }
      
      // Default to GBP (most common for this app)
      return '£';
    };

    const currencySymbol = getOrderCurrency();

    // Helper function to get item price
    const getItemPrice = (item) => {
      // Items in orders have a 'price' field (not price_gbp, price_usd, etc.)
      if (item.price !== undefined && item.price !== null) {
        return parseFloat(item.price);
      }
      // Fallback: try to get from currency-specific fields if they exist
      const { price } = getPrimaryCurrencyAndPrice(item);
      return price || 0;
    };

    return (
      <div className="modal-overlay order-modal-overlay" onClick={closeOrderModal}>
        <div className="modal-content order-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header order-modal-header">
            <h2>Order Details</h2>
            <button className="modal-close" onClick={closeOrderModal} aria-label="Close">×</button>
          </div>
          <div className="modal-body order-modal-body">
            <div className="order-details-container">
              <div className="detail-section order-info-section">
                <h3 className="section-title">
                  <span className="section-icon">📦</span>
                  Order Information
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Order ID</span>
                    <span className="detail-value order-id">#{selectedOrder.orderNumber || selectedOrder.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Order Date</span>
                    <span className="detail-value">{selectedOrder.date}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Order Status</span>
                    <span className="detail-value">
                      <span className={`status-badge ${selectedOrder.status?.toLowerCase() || 'pending'}`}>
                        {selectedOrder.status || 'Pending'}
                      </span>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Payment Status</span>
                    <span className="detail-value">
                      <span className={`status-badge payment-status ${selectedOrder.paymentStatus?.toLowerCase() || 'pending'}`}>
                        {selectedOrder.paymentStatus || 'Pending'}
                      </span>
                    </span>
                  </div>
                  <div className="detail-item total-item">
                    <span className="detail-label">Total Amount</span>
                    <span className="detail-value total-amount">{currencySymbol}{selectedOrder.total || '0.00'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section customer-info-section">
                <h3 className="section-title">
                  <span className="section-icon">👤</span>
                  Customer Information
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Customer Name</span>
                    <span className="detail-value">{selectedOrder.customer || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value email-value">{selectedOrder.customerEmail || 'N/A'}</span>
                  </div>
                  {selectedOrder.customerPhone && (
                    <div className="detail-item">
                      <span className="detail-label">Phone</span>
                      <span className="detail-value">{selectedOrder.customerPhone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section address-info-section">
                <h3 className="section-title">
                  <span className="section-icon">📍</span>
                  Shipping Address
                </h3>
                <div className="address-details">
                  {selectedOrder.customerAddress ? (
                    <div className="address-block">
                      <div className="address-line">
                        <span className="address-label">Address:</span>
                        <span className="address-value">{selectedOrder.customerAddress}</span>
                      </div>
                      <div className="address-line">
                        <span className="address-label">City:</span>
                        <span className="address-value">{selectedOrder.customerCity || 'N/A'}</span>
                      </div>
                      <div className="address-line">
                        <span className="address-label">State:</span>
                        <span className="address-value">{selectedOrder.customerState || 'N/A'}</span>
                      </div>
                      <div className="address-line">
                        <span className="address-label">ZIP Code:</span>
                        <span className="address-value">{selectedOrder.customerZipCode || 'N/A'}</span>
                      </div>
                      <div className="address-line">
                        <span className="address-label">Country:</span>
                        <span className="address-value">{selectedOrder.customerCountry || 'N/A'}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="no-address-message">
                      <p>No address information available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section items-section">
                <h3 className="section-title">
                  <span className="section-icon">🛍️</span>
                  Order Items
                </h3>
                <div className="order-items-list">
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    selectedOrder.items.map((item, index) => {
                      const itemPrice = getItemPrice(item);
                      const quantity = item.quantity || 1;
                      const totalPrice = itemPrice * quantity;
                      
                      return (
                        <div key={index} className="order-item-card">
                          <div className="item-main-info">
                            <span className="item-name">{item.name || 'Unknown Product'}</span>
                            <div className="item-meta">
                              <span className="item-price">
                                {currencySymbol}{totalPrice.toFixed(2)}
                              </span>
                              <span className="item-quantity">× {quantity}</span>
                            </div>
                          </div>
                          <div className="item-unit-price">
                            {currencySymbol}{itemPrice.toFixed(2)} each
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-items-message">
                      <p>No items available for this order</p>
                      <small>Items data may not be stored in the database</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer order-modal-footer">
            <div className="modal-footer-actions">
              {selectedOrder.status !== 'delivered' && (
                <button 
                  className="modal-btn primary" 
                  onClick={() => handleMarkAsDelivered(selectedOrder)}
                  disabled={loading}
                >
                  {loading ? 'Marking...' : 'Mark as Delivered'}
                </button>
              )}
              <button className="modal-btn secondary" onClick={closeOrderModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryModal = () => {
    const handleSubmit = (e) => {
      e.preventDefault();
      handleSaveCategory();
    };

    return (
      <div className="modal-overlay" onClick={closeCategoryModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
            <button className="modal-close" onClick={closeCategoryModal}>×</button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-group">
                <label htmlFor="category-name">Category Name</label>
                <input
                  type="text"
                  id="category-name"
                  name="name"
                  value={categoryFormData.name}
                  onChange={handleCategoryInputChange}
                  required
                  className="form-input"
                  placeholder="Enter category name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category-description">Description</label>
                <textarea
                  id="category-description"
                  name="description"
                  value={categoryFormData.description}
                  onChange={handleCategoryInputChange}
                  rows="3"
                  className="form-input"
                  placeholder="Enter category description"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category-status">Status</label>
                <select
                  id="category-status"
                  name="status"
                  value={categoryFormData.status}
                  onChange={handleCategoryInputChange}
                  className="form-input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeCategoryModal}>
              Cancel
            </button>
            <button className="modal-btn primary" onClick={handleSubmit}>
              {editingCategory ? 'Update Category' : 'Add Category'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSubcategoryModal = () => {
    const handleSubmit = (e) => {
      e.preventDefault();
      handleSaveSubcategory();
    };

    return (
      <div className="modal-overlay" onClick={closeSubcategoryModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}</h2>
            <button className="modal-close" onClick={closeSubcategoryModal}>×</button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit} className="subcategory-form">
              <div className="form-group">
                <label htmlFor="subcategory-name">Subcategory Name</label>
                <input
                  type="text"
                  id="subcategory-name"
                  name="name"
                  value={subcategoryFormData.name}
                  onChange={handleSubcategoryInputChange}
                  required
                  className="form-input"
                  placeholder="Enter subcategory name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subcategory-category">Parent Category</label>
                <select
                  id="subcategory-category"
                  name="category_id"
                  value={subcategoryFormData.category_id}
                  onChange={handleSubcategoryInputChange}
                  required
                  className="form-input"
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subcategory-description">Description</label>
                <textarea
                  id="subcategory-description"
                  name="description"
                  value={subcategoryFormData.description}
                  onChange={handleSubcategoryInputChange}
                  rows="3"
                  className="form-input"
                  placeholder="Enter subcategory description"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subcategory-status">Status</label>
                <select
                  id="subcategory-status"
                  name="status"
                  value={subcategoryFormData.status}
                  onChange={handleSubcategoryInputChange}
                  className="form-input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeSubcategoryModal}>
              Cancel
            </button>
            <button className="modal-btn primary" onClick={handleSubmit}>
              {editingSubcategory ? 'Update Subcategory' : 'Add Subcategory'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderShippingModal = () => {
    const handleSubmit = (e) => {
      e.preventDefault();
      handleSaveShipping();
    };

    return (
      <div className="modal-overlay" onClick={closeShippingModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingShipping ? 'Edit Shipping Rate' : 'Add Shipping Rate'}</h2>
            <button className="modal-close" onClick={closeShippingModal}>×</button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit} className="shipping-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="country_name">Country Name *</label>
                  <input
                    type="text"
                    id="country_name"
                    name="country_name"
                    value={shippingFormData.country_name}
                    onChange={handleShippingInputChange}
                    required
                    className="form-input"
                    placeholder="e.g., United States"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="country_code">Country Code</label>
                  <input
                    type="text"
                    id="country_code"
                    name="country_code"
                    value={shippingFormData.country_code}
                    onChange={handleShippingInputChange}
                    className="form-input"
                    placeholder="e.g., US"
                    maxLength="3"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="shipping_cost">Shipping Cost *</label>
                  <input
                    type="number"
                    id="shipping_cost"
                    name="shipping_cost"
                    value={shippingFormData.shipping_cost}
                    onChange={handleShippingInputChange}
                    step="0.01"
                    min="0"
                    required
                    className="form-input"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="estimated_delivery_days">Estimated Delivery Days</label>
                  <input
                    type="number"
                    id="estimated_delivery_days"
                    name="estimated_delivery_days"
                    value={shippingFormData.estimated_delivery_days}
                    onChange={handleShippingInputChange}
                    min="1"
                    max="30"
                    className="form-input"
                    placeholder="7"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={shippingFormData.is_active}
                      onChange={handleShippingInputChange}
                      className="form-checkbox"
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeShippingModal}>
              Cancel
            </button>
            <button className="modal-btn primary" onClick={handleSubmit}>
              {editingShipping ? 'Update Shipping Rate' : 'Add Shipping Rate'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQueryModal = () => {
    if (!selectedQuery) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content query-modal">
          <div className="modal-header">
            <h3>Contact Query Details</h3>
            <button className="close-btn" onClick={closeQueryModal}>
              ✕
            </button>
          </div>
          
          <div className="modal-body">
            <div className="query-details">
              <div className="detail-row">
                <label>Name:</label>
                <span>{selectedQuery.name || 'Unknown Name'}</span>
              </div>
              <div className="detail-row">
                <label>Email:</label>
                <span>{selectedQuery.email || 'No email provided'}</span>
              </div>
              <div className="detail-row">
                <label>Subject:</label>
                <span>{selectedQuery.subject || 'No subject'}</span>
              </div>
              <div className="detail-row">
                <label>Message:</label>
                <div className="message-content">{selectedQuery.message || 'No message content'}</div>
              </div>
              <div className="detail-row">
                <label>Submitted:</label>
                <span>{selectedQuery.created_at ? new Date(selectedQuery.created_at).toLocaleString() : 'Unknown date'}</span>
              </div>
            </div>
            
            {selectedQuery.admin_notes && (
              <div className="existing-notes">
                <h4>Existing Admin Notes:</h4>
                <div className="notes-content">
                  {selectedQuery.admin_notes}
                </div>
              </div>
            )}
            
            <div className="admin-actions">
              <div className="form-group">
                <label htmlFor="status">Status:</label>
                <select
                  id="status"
                  name="status"
                  value={queryFormData.status}
                  onChange={handleQueryInputChange}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="admin_notes">Add New Admin Notes:</label>
                <textarea
                  id="admin_notes"
                  name="admin_notes"
                  value={queryFormData.admin_notes}
                  onChange={handleQueryInputChange}
                  rows="4"
                  placeholder="Add new internal notes about this query..."
                ></textarea>
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button className="cancel-btn" onClick={closeQueryModal}>
              Cancel
            </button>
            <button className="save-btn" onClick={handleSaveQuery}>
              Update Query
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderVendorModal = () => {
    if (!selectedVendor) return null;

    const isApprove = vendorAction === 'approve';
    const isReject = vendorAction === 'reject';
    const isSuspend = vendorAction === 'suspend';
    const isReactivate = vendorAction === 'reactivate';
    const vendorName = selectedVendor.business_name || selectedVendor.email;

    const getActionIcon = () => {
      if (isApprove) return '✅';
      if (isReject) return '❌';
      if (isSuspend) return '⏸️';
      if (isReactivate) return '▶️';
      return '❓';
    };

    const getActionTitle = () => {
      if (isApprove) return 'Approve Vendor';
      if (isReject) return 'Reject Vendor';
      if (isSuspend) return 'Suspend Vendor';
      if (isReactivate) return 'Reactivate Vendor';
      return 'Vendor Action';
    };

    const getActionText = () => {
      if (isApprove) return 'approve';
      if (isReject) return 'reject';
      if (isSuspend) return 'suspend';
      if (isReactivate) return 'reactivate';
      return 'perform action on';
    };

    const getConsequenceText = () => {
      if (isApprove) return 'This vendor will be able to login and access their dashboard';
      if (isReject) return 'This vendor will be permanently deleted from the database and cannot sign up again with the same email';
      if (isSuspend) return 'This vendor will be temporarily suspended and cannot login';
      if (isReactivate) return 'This vendor will be able to login and access their dashboard again';
      return 'This action will affect the vendor account';
    };

    const getButtonClass = () => {
      if (isApprove || isReactivate) return 'approve';
      if (isReject || isSuspend) return 'reject';
      return '';
    };

    return (
      <div className="modal-overlay" onClick={closeVendorModal}>
        <div className="modal-content vendor-confirmation-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon">
              {getActionIcon()}
            </div>
            <h2>{getActionTitle()}</h2>
            <button className="modal-close" onClick={closeVendorModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="vendor-confirmation-content">
              <div className="confirmation-message">
                <p>
                  Are you sure you want to <strong>{getActionText()}</strong> the vendor account for:
                </p>
                <div className="vendor-details">
                  <div className="vendor-info-card">
                    <div className="vendor-name">{vendorName}</div>
                    <div className="vendor-email">{selectedVendor.email}</div>
                    {selectedVendor.phone && (
                      <div className="vendor-phone">{selectedVendor.phone}</div>
                    )}
                    <div className="vendor-date">
                      {isReactivate ? 'Suspended' : 'Requested'}: {new Date(selectedVendor.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="action-consequences">
                  <div className={`consequence ${getButtonClass()}`}>
                    <span className="consequence-icon">{getActionIcon()}</span>
                    <span>{getConsequenceText()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeVendorModal}>
              Cancel
            </button>
            <button 
              className={`modal-btn primary ${getButtonClass()}`}
              onClick={confirmVendorAction}
            >
              {getActionTitle()}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!showDeleteModal || !deleteTarget) return null;

    const getDeleteInfo = () => {
      switch (deleteType) {
        case 'product':
          return {
            title: 'Delete Product',
            icon: '🗑️',
            name: deleteTarget.name || 'Unnamed Product',
            description: 'This product will be permanently removed from the system.',
            warning: 'This action cannot be undone.'
          };
        case 'admin-product':
          return {
            title: 'Delete Admin Product',
            icon: '👑',
            name: deleteTarget.name || 'Unnamed Admin Product',
            description: 'This admin product will be permanently removed from the system.',
            warning: 'This action cannot be undone.'
          };
        case 'order':
          return {
            title: 'Delete Order',
            icon: '📦',
            name: `Order #${deleteTarget.orderNumber}`,
            description: 'This order will be permanently removed from the system.',
            warning: 'This action cannot be undone and will affect order history.'
          };
        case 'category':
          return {
            title: 'Delete Category',
            icon: '🏷️',
            name: deleteTarget.name,
            description: 'This category will be permanently removed from the system.',
            warning: 'This will affect all products in this category.'
          };
        case 'shipping':
          return {
            title: 'Delete Shipping Rate',
            icon: '🚚',
            name: deleteTarget.country_name,
            description: 'This shipping rate will be permanently removed from the system.',
            warning: 'This will affect shipping calculations for this country.'
          };
        case 'user':
          return {
            title: 'Delete Customer',
            icon: '👤',
            name: deleteTarget.name || deleteTarget.email,
            description: 'This customer will be permanently removed from the system.',
            warning: 'This action cannot be undone and will remove all customer data.'
          };
        default:
          return {
            title: 'Delete Item',
            icon: '❓',
            name: 'Unknown Item',
            description: 'This item will be permanently removed.',
            warning: 'This action cannot be undone.'
          };
      }
    };

    const deleteInfo = getDeleteInfo();

    return (
      <div className="modal-overlay" onClick={closeDeleteModal}>
        <div className="modal-content delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon">
              {deleteInfo.icon}
            </div>
            <h2>{deleteInfo.title}</h2>
            <button className="modal-close" onClick={closeDeleteModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="delete-confirmation-content">
              <div className="confirmation-message">
                <p>
                  Are you sure you want to delete <strong>{deleteInfo.name}</strong>?
                </p>
                <div className="item-details">
                  <div className="item-info-card">
                    <div className="item-name">{deleteInfo.name}</div>
                    <div className="item-description">{deleteInfo.description}</div>
                  </div>
                </div>
                <div className="delete-warning">
                  <div className="warning-message">
                    <span className="warning-icon">⚠️</span>
                    <span>{deleteInfo.warning}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeDeleteModal}>
              Cancel
            </button>
            <button 
              className="modal-btn primary delete"
              onClick={confirmDelete}
            >
              Delete {deleteType.charAt(0).toUpperCase() + deleteType.slice(1)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderNotification = () => {
    if (!notification) return null;

    const isSuccess = notification.type === 'success';
    const isError = notification.type === 'error';

    return (
      <div className={`notification ${notification.type}`}>
        <div className="notification-content">
          <div className="notification-icon">
            {isSuccess ? '✅' : isError ? '❌' : 'ℹ️'}
          </div>
          <div className="notification-message">
            {notification.message}
          </div>
          <button 
            className="notification-close"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
        <div className="notification-progress"></div>
      </div>
    );
  };

  const renderProductModal = () => {
    if (!editingProduct) return null;

    const handleSubmit = (e) => {
      e.preventDefault();
      handleSaveProduct();
    };

    return (
      <div className="modal-overlay" onClick={closeProductModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Edit Product</h2>
            <button className="modal-close" onClick={closeProductModal}>×</button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-group">
                <label htmlFor="name">Product Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={productFormData.name}
                  onChange={handleProductInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={productFormData.description}
                  onChange={handleProductInputChange}
                  rows="3"
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price_usd">Price (USD)</label>
                  <input
                    type="number"
                    id="price_usd"
                    name="price_usd"
                    value={productFormData.price_usd}
                    onChange={handleProductInputChange}
                    step="0.01"
                    min="0"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="price_gbp">Price (GBP)</label>
                  <input
                    type="number"
                    id="price_gbp"
                    name="price_gbp"
                    value={productFormData.price_gbp}
                    onChange={handleProductInputChange}
                    step="0.01"
                    min="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price_aed">Price (AED)</label>
                  <input
                    type="number"
                    id="price_aed"
                    name="price_aed"
                    value={productFormData.price_aed}
                    onChange={handleProductInputChange}
                    step="0.01"
                    min="0"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="price_sar">Price (SAR)</label>
                  <input
                    type="number"
                    id="price_sar"
                    name="price_sar"
                    value={productFormData.price_sar}
                    onChange={handleProductInputChange}
                    step="0.01"
                    min="0"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="stock">Stock</label>
                  <input
                    type="number"
                    id="stock"
                    name="stock"
                    value={productFormData.stock}
                    onChange={handleProductInputChange}
                    min="0"
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    name="category"
                    value={productFormData.category}
                    onChange={handleProductInputChange}
                    className="form-input"
                  >
                    <option value="">Select Category</option>
                    <option value="electronics">Electronics</option>
                    <option value="fashion">Fashion</option>
                    <option value="home">Home & Garden</option>
                    <option value="sports">Sports</option>
                    <option value="books">Books</option>
                    <option value="beauty">Beauty</option>
                    <option value="toys">Toys</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={productFormData.status}
                    onChange={handleProductInputChange}
                    className="form-input"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="image_url">Image URL</label>
                <input
                  type="url"
                  id="image_url"
                  name="image_url"
                  value={productFormData.image_url}
                  onChange={handleProductInputChange}
                  placeholder="https://example.com/image.jpg"
                  className="form-input"
                />
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeProductModal}>
              Cancel
            </button>
            <button className="modal-btn primary" onClick={handleSubmit}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAdminProductModal = () => {
    const handleSubmit = (e) => {
      e.preventDefault();
      handleSaveAdminProduct();
    };

    return (
      <div className="modal-overlay" onClick={closeAdminProductModal}>
        <div className="modal-content admin-product-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingAdminProduct ? 'Edit Admin Product' : 'Add Admin Product'}</h2>
            <button className="modal-close" onClick={closeAdminProductModal}>×</button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit} className="admin-product-form">
              {/* Basic Information */}
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-grid">
              <div className="form-group">
                    <label>Available Countries</label>
                    <div className="countries-section">
                      <div className="country-item">
                        <input 
                          type="checkbox" 
                          id="admin-country-uae"
                          checked={adminProductFormData.countries?.includes('UAE')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: [...(prev.countries || []), 'UAE'] 
                              }));
                            } else {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: (prev.countries || []).filter(country => country !== 'UAE') 
                              }));
                            }
                          }}
                        />
                        <label htmlFor="admin-country-uae">UAE</label>
                      </div>
                      <div className="country-item">
                        <input 
                          type="checkbox" 
                          id="admin-country-usa"
                          checked={adminProductFormData.countries?.includes('USA')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: [...(prev.countries || []), 'USA'] 
                              }));
                            } else {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: (prev.countries || []).filter(country => country !== 'USA') 
                              }));
                            }
                          }}
                        />
                        <label htmlFor="admin-country-usa">USA</label>
                      </div>
                      <div className="country-item">
                        <input 
                          type="checkbox" 
                          id="admin-country-uk"
                          checked={adminProductFormData.countries?.includes('UK')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: [...(prev.countries || []), 'UK'] 
                              }));
                            } else {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: (prev.countries || []).filter(country => country !== 'UK') 
                              }));
                            }
                          }}
                        />
                        <label htmlFor="admin-country-uk">UK</label>
                      </div>
                      <div className="country-item">
                        <input 
                          type="checkbox" 
                          id="admin-country-saudi"
                          checked={adminProductFormData.countries?.includes('Saudi Arabia')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: [...(prev.countries || []), 'Saudi Arabia'] 
                              }));
                            } else {
                              setAdminProductFormData(prev => ({ 
                                ...prev, 
                                countries: (prev.countries || []).filter(country => country !== 'Saudi Arabia') 
                              }));
                            }
                          }}
                        />
                        <label htmlFor="admin-country-saudi">Saudi Arabia</label>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Product Name *</label>
                <input
                  type="text"
                      placeholder="Enter product name"
                  value={adminProductFormData.name}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                    <label>Brand</label>
                    <input 
                      type="text" 
                      placeholder="Enter brand name"
                      value={adminProductFormData.brand}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, brand: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>SKU</label>
                    <input 
                      type="text" 
                      placeholder="Stock Keeping Unit"
                      value={adminProductFormData.sku}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, sku: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select 
                      value={adminProductFormData.category}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, category: e.target.value, subcategory: '' }))}
                      required
                      disabled={loading}
                    >
                      <option value="">
                        {loading ? 'Loading categories...' : 'Select a category'}
                      </option>
                      {categories.map(category => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Subcategory</label>
                    <select 
                      value={adminProductFormData.subcategory}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                      disabled={!adminProductFormData.category || loading}
                    >
                      <option value="">Select a subcategory (optional)</option>
                      {adminProductFormData.category && subcategories
                        .filter(sub => sub.categories?.name === adminProductFormData.category)
                        .map(subcategory => (
                          <option key={subcategory.id} value={subcategory.name}>
                            {subcategory.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="form-section">
                <h3>Pricing & Inventory</h3>
                <div className="form-grid">
                  {/* USD Prices - Show only if USA is selected */}
                  {adminProductFormData.countries?.includes('USA') && (
                    <>
                <div className="form-group">
                        <label>Current Price (USD) *</label>
                  <input
                    type="number"
                          step="0.01" 
                          placeholder="0.00"
                    value={adminProductFormData.price_usd}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, price_usd: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Original Price (USD)</label>
                        <input 
                          type="number" 
                    step="0.01"
                          placeholder="0.00"
                          value={adminProductFormData.original_price_usd}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, original_price_usd: e.target.value }))}
                  />
                </div>
                    </>
                  )}

                  {/* GBP Prices - Show only if UK is selected */}
                  {adminProductFormData.countries?.includes('UK') && (
                    <>
                <div className="form-group">
                        <label>Current Price (GBP) *</label>
                  <input
                    type="number"
                          step="0.01" 
                          placeholder="0.00"
                    value={adminProductFormData.price_gbp}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, price_gbp: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Original Price (GBP)</label>
                        <input 
                          type="number" 
                    step="0.01"
                          placeholder="0.00"
                          value={adminProductFormData.price_gbp}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, original_price_gbp: e.target.value }))}
                  />
                </div>
                    </>
                  )}

                  {/* AED Prices - Show only if UAE is selected */}
                  {adminProductFormData.countries?.includes('UAE') && (
                    <>
                <div className="form-group">
                        <label>Current Price (AED) *</label>
                  <input
                    type="number"
                          step="0.01" 
                          placeholder="0.00"
                    value={adminProductFormData.price_aed}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, price_aed: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Original Price (AED)</label>
                        <input 
                          type="number" 
                    step="0.01"
                          placeholder="0.00"
                          value={adminProductFormData.price_aed}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, original_price_aed: e.target.value }))}
                  />
                </div>
                    </>
                  )}

                  {/* SAR Prices - Show only if Saudi Arabia is selected */}
                  {adminProductFormData.countries?.includes('Saudi Arabia') && (
                    <>
                <div className="form-group">
                        <label>Current Price (SAR) *</label>
                  <input
                    type="number"
                          step="0.01" 
                          placeholder="0.00"
                    value={adminProductFormData.price_sar}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, price_sar: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Original Price (SAR)</label>
                        <input 
                          type="number" 
                    step="0.01"
                          placeholder="0.00"
                          value={adminProductFormData.price_sar}
                          onChange={(e) => setAdminProductFormData(prev => ({ ...prev, original_price_sar: e.target.value }))}
                  />
                </div>
                    </>
                  )}

                <div className="form-group">
                    <label>Stock *</label>
                  <input
                    type="number"
                      placeholder="Available quantity"
                    value={adminProductFormData.stock}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, stock: e.target.value }))}
                    min="0"
                    required
                  />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="form-section">
                <h3>Description</h3>
                <div className="form-grid">
                <div className="form-group">
                    <label>Short Description</label>
                    <textarea 
                      placeholder="Brief product description"
                      value={adminProductFormData.description}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Long Description</label>
                    <textarea 
                      placeholder="Detailed product description"
                      value={adminProductFormData.long_description}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, long_description: e.target.value }))}
                      rows="5"
                    />
                  </div>
                </div>
              </div>

              {/* Media */}
              <div className="form-section">
                <h3>Media</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Main Image URL</label>
                    <input 
                      type="url" 
                      placeholder="https://example.com/image.jpg"
                      value={adminProductFormData.image_url}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Video URL</label>
                    <input 
                      type="url" 
                      placeholder="https://example.com/video.mp4"
                      value={adminProductFormData.video_url}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, video_url: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="form-section">
                <h3>Product Details</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Weight</label>
                    <div className="weight-dimension-group">
                      <input 
                        type="number" 
                        placeholder="Weight value"
                        value={adminProductFormData.weight}
                        onChange={(e) => setAdminProductFormData(prev => ({ ...prev, weight: e.target.value }))}
                        step="0.01"
                        min="0"
                      />
                  <select
                        value={adminProductFormData.weight_unit}
                        onChange={(e) => setAdminProductFormData(prev => ({ ...prev, weight_unit: e.target.value }))}
                      >
                        <option value="g">Grams (g)</option>
                        <option value="kg">Kilograms (kg)</option>
                        <option value="lb">Pounds (lb)</option>
                        <option value="oz">Ounces (oz)</option>
                  </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Dimensions</label>
                    <div className="weight-dimension-group">
                      <input 
                        type="text" 
                        placeholder="L x W x H"
                        value={adminProductFormData.dimensions}
                        onChange={(e) => setAdminProductFormData(prev => ({ ...prev, dimensions: e.target.value }))}
                      />
                      <select 
                        value={adminProductFormData.dimension_unit}
                        onChange={(e) => setAdminProductFormData(prev => ({ ...prev, dimension_unit: e.target.value }))}
                      >
                        <option value="cm">Centimeters (cm)</option>
                        <option value="in">Inches (in)</option>
                        <option value="mm">Millimeters (mm)</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <input 
                      type="text" 
                      placeholder="Product color"
                      value={adminProductFormData.color}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, color: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Material</label>
                    <input 
                      type="text" 
                      placeholder="Product material"
                      value={adminProductFormData.material}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, material: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Warranty</label>
                    <input 
                      type="text" 
                      placeholder="Warranty information"
                      value={adminProductFormData.warranty}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, warranty: e.target.value }))}
                    />
                  </div>
                </div>
                </div>

              {/* Policies */}
              <div className="form-section">
                <h3>Policies</h3>
                <div className="form-grid">
                <div className="form-group">
                    <label>Shipping Information</label>
                    <textarea 
                      placeholder="Shipping details and policies"
                      value={adminProductFormData.shipping_info}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, shipping_info: e.target.value }))}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Return Policy</label>
                    <textarea 
                      placeholder="Return and refund policies"
                      value={adminProductFormData.return_policy}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, return_policy: e.target.value }))}
                      rows="3"
                    />
                  </div>
                </div>
              </div>

              {/* SEO */}
              <div className="form-section">
                <h3>SEO & Marketing</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Meta Title</label>
                    <input 
                      type="text" 
                      placeholder="SEO meta title"
                      value={adminProductFormData.meta_title}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Meta Description</label>
                    <textarea 
                      placeholder="SEO meta description"
                      value={adminProductFormData.meta_description}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                      rows="2"
                    />
                  </div>
                  <div className="form-group">
                    <label>SEO Keywords</label>
                    <input 
                      type="text" 
                      placeholder="Comma-separated keywords"
                      value={adminProductFormData.seo_keywords}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, seo_keywords: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="form-section">
                <h3>Status & Features</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Status</label>
                  <select
                    value={adminProductFormData.status}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                  <div className="form-group">
                    <label>Featured Product</label>
                    <input 
                      type="checkbox" 
                      checked={adminProductFormData.is_featured}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                    />
              </div>
              <div className="form-group">
                    <label>Best Seller</label>
                <input
                      type="checkbox" 
                      checked={adminProductFormData.is_bestseller}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, is_bestseller: e.target.checked }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>New Arrival</label>
                    <input 
                      type="checkbox" 
                      checked={adminProductFormData.is_new_arrival}
                      onChange={(e) => setAdminProductFormData(prev => ({ ...prev, is_new_arrival: e.target.checked }))}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeAdminProductModal}>
              Cancel
            </button>
            <button className="modal-btn draft" onClick={() => handleSaveAdminProductAsDraft()}>
              Save as Draft
            </button>
            <button className="modal-btn primary" onClick={handleSubmit}>
              {editingAdminProduct ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // User management functions
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = async (user) => {
    setDeleteTarget(user);
    setDeleteType('user');
    setShowDeleteModal(true);
  };

  const closeUserModal = () => {
    setSelectedUser(null);
    setShowUserModal(false);
  };

  // Change credentials functions
  const handleCredentialsInputChange = (e) => {
    const { name, value } = e.target;
    setChangeCredentialsFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChangeCredentials = async (e) => {
    e.preventDefault();
    
    // Validate current credentials
    if (changeCredentialsFormData.currentUsername !== adminInfo.username) {
      showNotification('Current username is incorrect.', 'error');
      return;
    }

    // Get stored password for validation
    const storedPassword = localStorage.getItem('adminPassword') || 'admin123';
    if (changeCredentialsFormData.currentPassword !== storedPassword) {
      showNotification('Current password is incorrect.', 'error');
      return;
    }

    // Validate new password confirmation
    if (changeCredentialsFormData.newPassword !== changeCredentialsFormData.confirmPassword) {
      showNotification('New password and confirmation password do not match.', 'error');
      return;
    }

    // Validate new username
    if (!changeCredentialsFormData.newUsername.trim()) {
      showNotification('New username cannot be empty.', 'error');
      return;
    }

    if (changeCredentialsFormData.newUsername === changeCredentialsFormData.currentUsername) {
      showNotification('New username must be different from current username.', 'error');
      return;
    }

    // Validate new password length
    if (changeCredentialsFormData.newPassword.length < 6) {
      showNotification('New password must be at least 6 characters long.', 'error');
      return;
    }

    try {
      // First, verify current credentials against Supabase table
      console.log('Fetching user with username:', changeCredentialsFormData.currentUsername);
      
      const { data: currentUser, error: fetchError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('username', changeCredentialsFormData.currentUsername)
        .single();

      console.log('Fetch result:', { currentUser, fetchError });

      if (fetchError) {
        console.error('Error fetching user:', fetchError);
        showNotification(`Error fetching user: ${fetchError.message}`, 'error');
        return;
      }

      if (!currentUser) {
        showNotification('Current username not found in database.', 'error');
        return;
      }

      // For now, we'll do a simple password check (in production, you should hash passwords)
      // This assumes the password in your table is stored as plain text for now
      if (currentUser.password_hash !== changeCredentialsFormData.currentPassword) {
        showNotification('Current password is incorrect.', 'error');
        return;
      }

      // Update credentials in Supabase table
      const updateData = {
        username: changeCredentialsFormData.newUsername,
        password_hash: changeCredentialsFormData.newPassword // In production, hash this password
      };

      console.log('Updating user with ID:', currentUser.id);
      console.log('Update data:', updateData);

      const { data: updateResult, error: updateError } = await supabase
        .from('admin_users')
        .update(updateData)
        .eq('id', currentUser.id)
        .select();

      if (updateError) {
        console.error('Error updating credentials in database:', updateError);
        console.error('Error details:', updateError.message, updateError.details, updateError.hint);
        showNotification(`Failed to update credentials: ${updateError.message}`, 'error');
        return;
      }

      console.log('Update successful:', updateResult);

      // Update the credentials in localStorage for AdminLogin component
      localStorage.setItem('adminUsername', changeCredentialsFormData.newUsername);
      localStorage.setItem('adminPassword', changeCredentialsFormData.newPassword);
      
      // Update admin info in state
      setAdminInfo(prev => ({
        ...prev,
        username: changeCredentialsFormData.newUsername
      }));

      // Reset form
      setChangeCredentialsFormData({
        currentUsername: '',
        newUsername: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      showNotification('Credentials updated successfully in database! Your new credentials are now active and will persist across sessions.', 'success');
    } catch (error) {
      console.error('Error updating credentials:', error);
      showNotification('Failed to update credentials. Please try again.', 'error');
    }
  };

  const renderChangeCredentials = () => (
    <div className="admin-change-credentials">
      <div className="section-header">
        <h2>Change Admin Credentials</h2>
        <p>Update your admin username and password</p>
      </div>
      
      <div className="credentials-form-container">
        <form onSubmit={handleChangeCredentials} className="credentials-form">
          <div className="form-section">
            <h3>Current Credentials</h3>
            <div className="form-group">
              <label htmlFor="currentUsername">Current Username</label>
              <input
                type="text"
                id="currentUsername"
                name="currentUsername"
                value={changeCredentialsFormData.currentUsername}
                onChange={handleCredentialsInputChange}
                placeholder="Enter current username"
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={changeCredentialsFormData.currentPassword}
                onChange={handleCredentialsInputChange}
                placeholder="Enter current password"
                required
                className="form-input"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>New Credentials</h3>
            <div className="form-group">
              <label htmlFor="newUsername">New Username</label>
              <input
                type="text"
                id="newUsername"
                name="newUsername"
                value={changeCredentialsFormData.newUsername}
                onChange={handleCredentialsInputChange}
                placeholder="Enter new username"
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={changeCredentialsFormData.newPassword}
                onChange={handleCredentialsInputChange}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength="6"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={changeCredentialsFormData.confirmPassword}
                onChange={handleCredentialsInputChange}
                placeholder="Confirm new password"
                required
                className="form-input"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="save-btn">
              Update Credentials
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderUserModal = () => {
    if (!selectedUser) return null;

    return (
      <div className="modal-overlay" onClick={closeUserModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Customer Details</h3>
            <button className="close-btn" onClick={closeUserModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="user-details">
              <div className="detail-row">
                <label>Customer ID:</label>
                <span>{selectedUser.id}</span>
              </div>
              <div className="detail-row">
                <label>Name:</label>
                <span>{selectedUser.name}</span>
              </div>
              <div className="detail-row">
                <label>Email:</label>
                <span>{selectedUser.email}</span>
              </div>
              <div className="detail-row">
                <label>Phone:</label>
                <span>{selectedUser.phone || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <label>Status:</label>
                <span>
                  <span className={`status-badge ${selectedUser.status || 'active'}`}>
                    {selectedUser.status || 'Active'}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <label>Created:</label>
                <span>{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : 'N/A'}</span>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={closeUserModal}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!adminInfo) {
    return (
      <div className="admin-dashboard-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-page">
      <div className="admin-dashboard-container">
        <div className="dashboard-sidebar">
          <div className="admin-info">
            <h3>Admin Panel</h3>
            <p>Welcome, {adminInfo.username}!</p>
          </div>
          
          <nav className="dashboard-nav">
            <button 
              className={`nav-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              <span>📊</span> Overview
            </button>

            <button 
              className={`nav-item ${activeSection === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveSection('orders')}
            >
              <span>🛒</span> Orders
            </button>
            <button 
              className={`nav-item ${activeSection === 'products' ? 'active' : ''}`}
              onClick={() => setActiveSection('products')}
            >
              <span>📦</span> Products
            </button>
            <button 
              className={`nav-item ${activeSection === 'drafts' ? 'active' : ''}`}
              onClick={() => setActiveSection('drafts')}
            >
              <span>📝</span> Drafts
              <span className="drafts-count">({products.filter(p => p.status === 'draft').length})</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'add-product' ? 'active' : ''}`}
              onClick={() => setActiveSection('add-product')}
            >
              <span>➕</span> Add Product
            </button>
            <button 
              className={`nav-item ${activeSection === 'admin-products' ? 'active' : ''}`}
              onClick={() => setActiveSection('admin-products')}
            >
              <span>👑</span> Admin Products
              <span className="admin-products-count">({adminProducts.length})</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'featured-products' ? 'active' : ''}`}
              onClick={() => setActiveSection('featured-products')}
            >
              <span>⭐</span> Featured Products
            </button>
            <button 
              className={`nav-item ${activeSection === 'featured-category' ? 'active' : ''}`}
              onClick={() => setActiveSection('featured-category')}
            >
              <span>🏷️</span> Featured Category
            </button>
            <button 
              className={`nav-item ${activeSection === 'hot-deals' ? 'active' : ''}`}
              onClick={() => setActiveSection('hot-deals')}
            >
              <span>🔥</span> Hot Deals
            </button>
            <button 
              className={`nav-item ${activeSection === 'users' ? 'active' : ''}`}
              onClick={() => setActiveSection('users')}
            >
              <span>👥</span> Users
            </button>
            <button 
              className={`nav-item ${activeSection === 'categories' ? 'active' : ''}`}
              onClick={() => setActiveSection('categories')}
            >
              <span>🏷️</span> Categories
            </button>
            <button 
              className={`nav-item ${activeSection === 'vendor-requests' ? 'active' : ''}`}
              onClick={() => setActiveSection('vendor-requests')}
            >
              <span>📋</span> Vendor Requests
              {vendorRequests.length > 0 && (
                <span className="request-badge">{vendorRequests.length}</span>
              )}
            </button>
            <button 
              className={`nav-item ${activeSection === 'vendors' ? 'active' : ''}`}
              onClick={() => setActiveSection('vendors')}
            >
              <span>👥</span> Vendors
              <span className="vendor-count">({approvedVendors.length})</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'shipping' ? 'active' : ''}`}
              onClick={() => setActiveSection('shipping')}
            >
              <span>🚚</span> Shipping
              <span className="shipping-count">({shippingRates.length})</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'queries' ? 'active' : ''}`}
              onClick={() => setActiveSection('queries')}
            >
              <span>📧</span> Admin Queries
              <span className="queries-count">({contactQueries.length})</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'site-settings' ? 'active' : ''}`}
              onClick={() => setActiveSection('site-settings')}
            >
              <span>⚙️</span> Site Settings
            </button>
            <button 
              className={`nav-item ${activeSection === 'change-credentials' ? 'active' : ''}`}
              onClick={() => setActiveSection('change-credentials')}
            >
              <span>🔐</span> Change Credentials
            </button>
          </nav>
          
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span> Logout
          </button>
        </div>

        <div className="dashboard-main">
          <div className="dashboard-header">
            <h2>{activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}</h2>
            <button 
              className="refresh-btn" 
              onClick={loadAdminData}
              disabled={loading}
            >
              {loading ? '🔄 Loading...' : '🔄 Refresh Data'}
            </button>
          </div>
          <div className="dashboard-content">
            {activeSection === 'overview' && renderOverview()}
            {activeSection === 'orders' && renderOrders()}
            {activeSection === 'products' && renderProducts()}
            {activeSection === 'add-product' && renderAddProduct()}
            {activeSection === 'admin-products' && renderAdminProducts()}
            {activeSection === 'featured-products' && renderFeaturedProducts()}
            {activeSection === 'featured-category' && renderFeaturedCategory()}
            {activeSection === 'hot-deals' && renderHotDeals()}
            {activeSection === 'users' && renderUsers()}
            {activeSection === 'categories' && renderCategories()}
            {activeSection === 'vendor-requests' && renderVendorRequests()}
            {activeSection === 'vendors' && renderApprovedVendors()}
            {activeSection === 'shipping' && renderShipping()}
            {activeSection === 'queries' && renderQueries()}
            {activeSection === 'site-settings' && renderSiteSettings()}
            {activeSection === 'change-credentials' && renderChangeCredentials()}
            {activeSection === 'drafts' && renderDrafts()}
          </div>
        </div>
      </div>
      {showOrderModal && renderOrderModal()}
      {showProductModal && renderProductModal()}
      {showAdminProductModal && renderAdminProductModal()}
      {showCategoryModal && renderCategoryModal()}
      {showSubcategoryModal && renderSubcategoryModal()}
      {showVendorModal && renderVendorModal()}
      {showShippingModal && renderShippingModal()}
      {showQueryModal && renderQueryModal()}
      {showDeleteModal && renderDeleteModal()}
      {showUserModal && renderUserModal()}
      {notification && renderNotification()}
    </div>
  );
};

export default AdminDashboard; 